import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import '../App.css';
import ReactGA from 'react-ga4';
import { translations } from '../Translations'; 
import { supabase } from '../supabase';
import { getCourseLessons } from '../Data/LessonData';
import { INDIAN_EXAMS } from '../Data/ExamData';
import { translateKeystroke } from '../Data/KeyboardEngine';
import { PASSAGE_LIBRARY } from '../Data/PassageLibrary';
import { motion } from 'framer-motion';
// 🚀 FIXED: Added playSound back so the 3 arcade games don't crash!
import { initAudio, playKeystroke, playSound } from '../Data/SoundManager'; 
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// 🚀 FIXED: Correct relative paths for the lazy loaded components
const ActiveTypingTest = lazy(() => import('./ActiveTypingTest'));
const AdvancedTypingTest = lazy(() => import('./AdvancedTypingTest'));
const KeyCapSurvival = lazy(() => import('./KeyCapSurvival'));
const SyllableSlasher = lazy(() => import('./SyllableSlasher'));
const RhythmTypist = lazy(() => import('./RhythmTypist'));
const BridgeRunner = lazy(() => import('./BridgeRunner'));
const TugOfWar = lazy(() => import('./TugOfWar'));

// ==========================================
// 🎉 FLASH SALE PROMO BANNER
// ==========================================
function PromoBanner({ isDarkMode, setMenuTab }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user already dismissed it
    const isDismissed = localStorage.getItem('dismissed_freedom50');
    if (!isDismissed) setIsVisible(true);
  }, []);

  if (!isVisible) return null;

  return (
    <div style={{ width: '100%', maxWidth: '1100px', margin: '0 auto 20px auto', background: 'linear-gradient(90deg, #f59e0b, #ea580c)', color: 'white', padding: '12px 20px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box', boxShadow: '0 10px 25px rgba(234, 88, 12, 0.3)', zIndex: 50 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '24px' }}>🪔</span>
        <div style={{ fontSize: '15px', fontWeight: '500' }}>
          <strong style={{ fontSize: '16px', letterSpacing: '0.5px' }}>DIWALI DHAMAKA SALE!</strong> 
          {' '}Get 50% OFF the Annual Premium Plan. 
        </div>
        <button 
          onClick={() => setMenuTab("upgrade")}
          style={{ backgroundColor: 'white', color: '#ea580c', padding: '6px 12px', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'transform 0.1s' }}
          onMouseDown={e => e.target.style.transform = 'scale(0.95)'}
          onMouseUp={e => e.target.style.transform = 'scale(1)'}
        >
          CLAIM OFFER ➔
        </button>
      </div>
      <button onClick={() => { setIsVisible(false); localStorage.setItem('dismissed_diwali50', 'true'); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', padding: '0 5px', fontWeight: 'bold', opacity: 0.8 }}>
        &times;
      </button>
    </div>
  );
}

// ==========================================
// 💳 RAZORPAY SCRIPT LOADER
// ==========================================
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// ==========================================
// 🗣️ THE FEEDBACK MODAL
// ==========================================
function FeedbackModal({ currentUser, isDarkMode, isOpen, setIsOpen }) {
  const [step, setStep] = useState("prompt"); 
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!feedbackText.trim()) return;
    setIsSubmitting(true);
    try {
      await supabase.from('feedback').insert([{ username: currentUser, message: feedbackText }]);
      setStep("thanks");
      setTimeout(() => {
        setIsOpen(false);
        setStep("prompt"); 
        setFeedbackText("");
      }, 3000); 
    } catch (error) {
      console.error("Feedback error", error);
    }
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  const tStyle = {
    bg: isDarkMode ? '#1e293b' : 'white',
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#475569',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    inputBg: isDarkMode ? '#0f172a' : '#f8fafc'
  };

  return (
    <div style={{ position: 'fixed', bottom: '30px', right: '30px', width: '320px', backgroundColor: tStyle.bg, border: `1px solid ${tStyle.border}`, borderRadius: '16px', padding: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 9999, fontFamily: 'sans-serif' }}>
      {step === "prompt" && (
        <div>
          <h3 style={{ margin: '0 0 10px 0', color: tStyle.text, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>👋 Enjoying My Typing Coach?</h3>
          <p style={{ margin: '0 0 15px 0', color: tStyle.subtext, fontSize: '14px', lineHeight: '1.4' }}>We'd love to hear your thoughts so we can make the app even better!</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={() => setStep("form")} style={{ padding: '10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Give Feedback</button>
            <button onClick={() => setIsOpen(false)} style={{ padding: '8px', backgroundColor: 'transparent', color: tStyle.subtext, border: `1px solid ${tStyle.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}>Close</button>
          </div>
        </div>
      )}
      {step === "form" && (
        <div>
          <h3 style={{ margin: '0 0 10px 0', color: tStyle.text, fontSize: '16px' }}>Your Feedback</h3>
          <textarea autoFocus placeholder="What do you love? What could be improved?" value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} style={{ width: '100%', height: '100px', backgroundColor: tStyle.inputBg, color: tStyle.text, border: `1px solid ${tStyle.border}`, borderRadius: '8px', padding: '10px', boxSizing: 'border-box', marginBottom: '10px', resize: 'none', outline: 'none' }} />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => setIsOpen(false)} style={{ padding: '10px', backgroundColor: 'transparent', color: tStyle.subtext, border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={isSubmitting || !feedbackText.trim()} style={{ flex: 1, padding: '10px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', opacity: (isSubmitting || !feedbackText.trim()) ? 0.6 : 1 }}>
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </div>
      )}
      {step === "thanks" && (
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ fontSize: '30px', marginBottom: '10px' }}>💚</div>
          <h3 style={{ margin: '0 0 5px 0', color: tStyle.text, fontSize: '16px' }}>Thank You!</h3>
          <p style={{ margin: 0, color: tStyle.subtext, fontSize: '14px' }}>Your feedback has been saved.</p>
        </div>
      )}
    </div>
  );
}

// ==========================================
// 🧠 SMART KEYBOARD TIP MODAL
// ==========================================
function KeyboardTipModal({ isOpen, onClose, isDarkMode, layoutName }) {
  if (!isOpen) return null;

  const tStyle = {
    bg: isDarkMode ? '#1e293b' : 'white',
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    highlight: '#0ea5e9'
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', backdropFilter: 'blur(3px)' }}>
      <div style={{ backgroundColor: tStyle.bg, border: `2px solid ${tStyle.highlight}`, borderRadius: '20px', padding: '30px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
        <div style={{ fontSize: '50px', marginBottom: '15px' }}>✨</div>
        <h2 style={{ color: tStyle.text, margin: '0 0 10px 0', fontSize: '24px' }}>Smart Typing Activated</h2>
        <p style={{ color: tStyle.subtext, fontSize: '15px', lineHeight: '1.6', marginBottom: '25px' }}>
          You selected <b>{layoutName}</b>. Our platform uses a built-in translation engine! 
          <br/><br/>
          You <b>do not</b> need to change your Windows or Mac system language settings. Just use your standard English keyboard, and we will handle the magic.
        </p>
        <button onClick={onClose} style={{ width: '100%', padding: '14px', backgroundColor: tStyle.highlight, color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(14, 165, 233, 0.4)' }}>
          Got it, let's type!
        </button>
        <style>{`@keyframes popIn { 0% { transform: scale(0.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }`}</style>
      </div>
    </div>
  );
}

// ==========================================
// 🎮 ARCADE LESSON MAPPING
// ==========================================
const ARCADE_LESSONS = {
  1: { title: "Lesson 1: Home Row", caps: false, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';'] },
  2: { title: "Lesson 2: Top (E, I)", caps: false, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i'] },
  3: { title: "Lesson 3: Top (R, U)", caps: false, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u'] },
  4: { title: "Lesson 4: Top (T, O)", caps: false, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o'] },
  5: { title: "Lesson 5: Capitals & (.)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.'] },
  6: { title: "Lesson 6: Bottom (C, ,)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ','] },
  7: { title: "Lesson 7: Center (G, H)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ',', 'g', 'h', "'"] },
  8: { title: "Lesson 8: Bottom (V, N)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ',', 'g', 'h', "'", 'v', 'n', '?'] },
  9: { title: "Lesson 9: Reaches (W, M)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ',', 'g', 'h', "'", 'v', 'n', '?', 'w', 'm'] },
  10: { title: "Lesson 10: Outer (Q, P)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ',', 'g', 'h', "'", 'v', 'n', '?', 'w', 'm', 'q', 'p'] },
  11: { title: "Lesson 11: Stretches (B, Y)", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ',', 'g', 'h', "'", 'v', 'n', '?', 'w', 'm', 'q', 'p', 'b', 'y'] },
  12: { title: "Lesson 12: Full Keyboard", caps: true, chars: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', 'e', 'i', 'r', 'u', 't', 'o', '.', 'c', ',', 'g', 'h', "'", 'v', 'n', '?', 'w', 'm', 'q', 'p', 'b', 'y', 'z', 'x'] }
};

// ==========================================
// 🎈 BALLOON ASCENT GAME ENGINE (V11 - Mobile Fixed)
// ==========================================
function BalloonGame({ isDarkMode, onExit, arcadeLevel, currentUser, soundEnabled }) {
  const [gameState, setGameState] = useState({ balloons: [], lifelines: 5, score: 0, isPlaying: false });
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameOverPhase, setGameOverPhase] = useState('none'); 
  const [playerName, setPlayerName] = useState(currentUser ? currentUser.split('@')[0] : 'Guest');

  // 🚀 Leaderboard States
  const [leaderboardView, setLeaderboardView] = useState('personal'); 
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [isFetchingGlobal, setIsFetchingGlobal] = useState(false);

  // 🚀 Hidden Input Reference for Mobile Keyboard
  const hiddenInputRef = useRef(null);

  const currentLessonData = ARCADE_LESSONS[arcadeLevel] || ARCADE_LESSONS[1];
  const activeLetters = currentLessonData.chars;
  const allowCapitals = currentLessonData.caps;

  useEffect(() => {
    const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
    const userKey = currentUser || 'Guest';
    const userDb = db[userKey] || {};
    setLeaderboard(userDb.arcadeLeaderboard || []);
    if (currentUser) setPlayerName(currentUser.split('@')[0]);
  }, [currentUser]);

  // 🎯 CORE BALLOON POPPING HANDLER (Used by BOTH Desktop & Mobile)
  const handlePopChar = useCallback((pressedChar) => {
    setGameState((prev) => {
      if (!prev.isPlaying) return prev;
      const matchIndex = prev.balloons.findIndex((b) => b.letter === pressedChar);
      if (matchIndex !== -1) {
        playSound('pop', soundEnabled);
        return {
          ...prev,
          score: prev.score + 10,
          balloons: prev.balloons.filter((_, idx) => idx !== matchIndex)
        };
      } else {
        playSound('error', soundEnabled);
        return { ...prev, score: Math.max(0, prev.score - 5) };
      }
    });
  }, [soundEnabled]);

  const startGame = () => {
    setGameOverPhase('none');
    setGameState({ balloons: [], lifelines: 5, score: 0, isPlaying: true });
    setTimeout(() => {
      hiddenInputRef.current?.focus();
    }, 50);
  };

  // 🚀 Spawner Loop
  useEffect(() => {
    if (!gameState.isPlaying) return;
    const spawner = setInterval(() => {
      setGameState((prev) => {
        if (!prev.isPlaying) return prev;
        const maxBalloons = prev.score >= 200 ? 3 : (prev.score >= 80 ? 2 : 1);
        const numToSpawn = Math.floor(Math.random() * maxBalloons) + 1; 
        const newBalloons = [];
        for (let i = 0; i < numToSpawn; i++) {
          const baseLetter = activeLetters[Math.floor(Math.random() * activeLetters.length)];
          const isCapital = allowCapitals && Math.random() > 0.5; 
          const targetLetter = isCapital ? baseLetter.toUpperCase() : baseLetter;
          newBalloons.push({
            id: Date.now() + Math.random(), 
            letter: targetLetter,
            left: Math.floor(Math.random() * 80) + 10,
            bottom: -5 - (Math.random() * 15) 
          });
        }
        return { ...prev, balloons: [...prev.balloons, ...newBalloons] };
      });
    }, 1500); 
    return () => clearInterval(spawner);
  }, [gameState.isPlaying, activeLetters, allowCapitals]);

  // 🚀 Balloon Float / Miss Loop
  useEffect(() => {
    if (!gameState.isPlaying) return;
    const gameLoop = setInterval(() => {
      setGameState((prev) => {
        if (!prev.isPlaying) return prev;
        let missedCount = 0;
        const updatedBalloons = [];
        const currentSpeed = 0.2 + (prev.score * 0.0005); 
        for (const b of prev.balloons) {
          const newBottom = b.bottom + currentSpeed;
          if (newBottom > 110) missedCount += 1;
          else updatedBalloons.push({ ...b, bottom: newBottom });
        }
        if (missedCount > 0) {
          playSound('error', soundEnabled);
          const newLifelines = prev.lifelines - missedCount;
          if (newLifelines <= 0) {
            setGameOverPhase('nameEntry'); 
            return { ...prev, balloons: updatedBalloons, lifelines: newLifelines, isPlaying: false };
          }
          return { ...prev, balloons: updatedBalloons, lifelines: newLifelines };
        }
        return { ...prev, balloons: updatedBalloons };
      });
    }, 16);
    return () => clearInterval(gameLoop);
  }, [gameState.isPlaying, soundEnabled]);

  // 🚀 Desktop Physical Keyboard Listener
  useEffect(() => {
    if (!gameState.isPlaying) return;
    const handleKeyDown = (e) => {
      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        handlePopChar(e.key);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState.isPlaying, handlePopChar]);

  // 🚀 Auto-focus mobile input when game enters play state
  useEffect(() => {
    if (gameState.isPlaying && hiddenInputRef.current) {
      hiddenInputRef.current.focus();
    }
  }, [gameState.isPlaying]);

  const handleSaveScore = async () => {
    if (gameState.score > 0) {
      const finalScore = gameState.score;
      const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      const userKey = currentUser || 'Guest';
      
      if (!db[userKey]) db[userKey] = { arcadeLeaderboard: [] };
      if (!db[userKey].arcadeLeaderboard) db[userKey].arcadeLeaderboard = [];
      
      const currentBoard = db[userKey].arcadeLeaderboard;
      const otherLevelScores = currentBoard.filter(s => s.levelId !== Number(arcadeLevel));
      
      let thisLevelScores = currentBoard.filter(s => s.levelId === Number(arcadeLevel));
      thisLevelScores.push({ name: playerName.trim() || 'Guest', email: currentUser || 'Guest', score: finalScore, levelId: Number(arcadeLevel), date: Date.now() });
      
      thisLevelScores.sort((a, b) => b.score - a.score);
      thisLevelScores = thisLevelScores.slice(0, 10);
      
      const updatedBoard = [...otherLevelScores, ...thisLevelScores];
      db[userKey].arcadeLeaderboard = updatedBoard;
      
      localStorage.setItem('typeFasterDB', JSON.stringify(db));
      setLeaderboard(updatedBoard);
      
      try { 
        if (currentUser) {
          await supabase.from('users').update({ progress_data: db[userKey] }).eq('email', currentUser); 
        }
      } catch (err) {}
    }
    setGameOverPhase('leaderboard');
  };

  const fetchGlobalLeaderboard = useCallback(async () => {
    setIsFetchingGlobal(true);
    try {
      const { data } = await supabase.from('users').select('progress_data');
      let allScores = [];

      if (data) {
        data.forEach(user => {
          const userScores = user.progress_data?.arcadeLeaderboard || [];
          const levelScores = userScores.filter(s => s.levelId === Number(arcadeLevel));
          allScores.push(...levelScores);
        });
      }

      const localDb = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      if (localDb['Guest'] && localDb['Guest'].arcadeLeaderboard) {
        allScores.push(...localDb['Guest'].arcadeLeaderboard.filter(s => s.levelId === Number(arcadeLevel)));
      }

      allScores.sort((a, b) => b.score - a.score);

      const uniqueScores = [];
      const seenAccounts = new Set();
      
      for (const s of allScores) {
        const accountId = s.email || s.name; 
        if (!seenAccounts.has(accountId)) {
          uniqueScores.push(s);
          seenAccounts.add(accountId);
        }
      }

      setGlobalLeaderboard(uniqueScores);
    } catch (e) {
      console.error("Error fetching global leaderboard", e);
    }
    setIsFetchingGlobal(false);
  }, [arcadeLevel]);

  useEffect(() => {
    const isBoardOpen = gameOverPhase === 'leaderboard';
    if (isBoardOpen && leaderboardView === 'global') {
      fetchGlobalLeaderboard();
    }
  }, [gameOverPhase, leaderboardView, fetchGlobalLeaderboard]);

  const personalScores = leaderboard.filter(s => s.levelId === Number(arcadeLevel)).sort((a, b) => b.score - a.score).slice(0, 10);
  const top99 = globalLeaderboard.slice(0, 99);
  
  let myGlobalRank = -1;
  let myBestScore = null;
  
  const myIndex = globalLeaderboard.findIndex(s => 
    (currentUser && s.email === currentUser) || 
    (!currentUser && s.name === (playerName.trim() || 'Guest'))
  );
  
  if (myIndex !== -1) {
    myGlobalRank = myIndex + 1;
    myBestScore = globalLeaderboard[myIndex].score;
  }

  const tStyle = {
    bg: isDarkMode ? '#0f172a' : '#f1f5f9', 
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    highlight: '#0ea5e9',                       
    border: isDarkMode ? '#334155' : '#e2e8f0',
    sky: isDarkMode ? '#1e293b' : '#e0f2fe',
    popBg: isDarkMode ? '#0f172a' : '#ffffff',
    inputBg: isDarkMode ? '#1e293b' : '#f1f5f9',
  };

  const { balloons, lifelines, score, isPlaying } = gameState;

  return (
    <div 
      onClick={() => hiddenInputRef.current?.focus()} 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: tStyle.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}
    >
      
      {/* 🚀 BULLETPROOF MOBILE INPUT */}
      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck="false"
        style={{
          position: 'absolute',
          opacity: 0,
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          cursor: 'default',
          caretColor: 'transparent'
        }}
        onChange={(e) => {
          const val = e.target.value;
          if (val) {
            const lastChar = val.slice(-1);
            handlePopChar(lastChar);
            e.target.value = '';
          }
        }}
      />

      <div style={{ width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'space-between', marginBottom: '20px', marginTop: '10px', zIndex: 2 }}>
        <button onClick={onExit} style={{ padding: '10px 20px', backgroundColor: tStyle.popBg, color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>⬅️ Exit Arcade</button>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: tStyle.text, display: 'flex', gap: '25px', alignItems: 'center' }}>
          <span style={{ fontSize: '15px', color: '#64748b', alignSelf: 'center', marginRight: '10px', borderRight: `2px solid ${tStyle.border}`, paddingRight: '15px' }}>{currentLessonData.title}</span>
          <span>Score: <span style={{color: '#0ea5e9', fontSize: '24px'}}>{score}</span></span> 
          <span>Lifelines: <span style={{ fontSize: '22px'}}>{'❤️'.repeat(Math.max(0, lifelines))}</span></span>
        </div>
      </div>

      <div style={{ position: 'relative', width: '100%', maxWidth: '900px', flexGrow: 1, minHeight: '400px', backgroundColor: tStyle.sky, border: `4px solid ${tStyle.border}`, borderRadius: '24px', overflow: 'hidden', boxShadow: 'inset 0 0 30px rgba(0,0,0,0.1), 0 10px 30px rgba(0,0,0,0.2)', zIndex: 2 }}>
        {!isPlaying && gameOverPhase === 'none' && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10, position: 'relative' }}>
            <div style={{ fontSize: '60px', marginBottom: '15px' }}>🎈</div>
            <h2 style={{ color: 'white', fontSize: '40px', margin: '0 0 10px 0', textShadow: '0 4px 8px rgba(0,0,0,0.5)' }}>Balloon Ascent</h2>
            <p style={{ color: '#bae6fd', fontSize: '18px', marginBottom: '30px' }}>{currentLessonData.title} Mode</p>
            <button onClick={startGame} style={{ padding: '16px 40px', fontSize: '24px', fontWeight: 'bold', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '16px', cursor: 'pointer', boxShadow: '0 8px 20px rgba(34, 197, 94, 0.4)' }}>Start Game ▶️</button>
          </div>
        )}

        {gameOverPhase === 'nameEntry' && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 20, position: 'absolute', top: 0, left: 0, width: '100%', padding: '20px', boxSizing: 'border-box' }}>
            <div style={{ width: '100%', maxWidth: '350px', backgroundColor: tStyle.popBg, borderRadius: '16px', padding: '30px', border: `3px solid ${isDarkMode ? '#ef4444' : '#dc2626'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              <h2 style={{ color: isDarkMode ? '#fca5a5' : '#b91c1c', margin: '0 0 5px 0', fontSize: '28px' }}>Game Over!</h2>
              <p style={{ color: tStyle.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '25px' }}>Final Score: <span style={{ color: '#0ea5e9', fontSize: '28px' }}>{score}</span></p>
              <label style={{ alignSelf: 'flex-start', fontSize: '13px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Who is playing?</label>
              <input type="text" value={playerName} onChange={(e) => setPlayerName(e.target.value)} maxLength={15} style={{ width: '100%', padding: '15px', borderRadius: '10px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.text, fontWeight: 'bold', fontSize: '18px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box', textAlign: 'center' }} />
              <button onClick={handleSaveScore} style={{ width: '100%', padding: '15px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>Save Score</button>
            </div>
          </div>
        )}

        {/* TWO-TAB LEADERBOARD UI */}
        {gameOverPhase === 'leaderboard' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 20, padding: '20px' }}>
            <div style={{ width: '100%', maxWidth: '450px', backgroundColor: tStyle.popBg, borderRadius: '16px', padding: '25px', border: `3px solid ${tStyle.highlight}`, display: 'flex', flexDirection: 'column', maxHeight: '95%', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button onClick={() => setLeaderboardView('personal')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', backgroundColor: leaderboardView === 'personal' ? tStyle.highlight : tStyle.inputBg, color: leaderboardView === 'personal' ? 'white' : tStyle.subtext, transition: '0.2s' }}>
                  👤 Personal (10)
                </button>
                <button onClick={() => setLeaderboardView('global')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', backgroundColor: leaderboardView === 'global' ? tStyle.highlight : tStyle.inputBg, color: leaderboardView === 'global' ? 'white' : tStyle.subtext, transition: '0.2s' }}>
                  🌍 Global (99)
                </button>
              </div>

              <h2 style={{ textAlign: 'center', color: tStyle.highlight, margin: '0 0 15px 0', fontSize: '20px' }}>
                {leaderboardView === 'personal' ? 'Your Best Ascents' : 'World High Flyers'} <br/> 
                <span style={{ fontSize: '14px', color: tStyle.subtext }}>{currentLessonData.title}</span>
              </h2>

              <div style={{ flexGrow: 1, overflowY: 'auto', marginBottom: '15px', paddingRight: '5px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                
                {leaderboardView === 'personal' ? (
                  personalScores.length === 0 ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>No scores for this lesson yet.</p> : (
                    personalScores.map((entry, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', backgroundColor: index % 2 === 0 ? (isDarkMode ? '#1e293b' : '#f8fafc') : 'transparent', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', color: tStyle.text }}>
                        <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <span style={{ color: tStyle.subtext, width: '25px', fontSize: '14px' }}>#{index + 1}</span>
                          <span style={{ color: entry.name === playerName ? tStyle.highlight : tStyle.text }}>{entry.name}</span>
                        </span>
                        <span style={{ color: '#22c55e', fontSize: '18px' }}>{entry.score} pts</span>
                      </div>
                    ))
                  )
                ) : (
                  isFetchingGlobal ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>📡 Fetching scores...</p> : (
                    top99.length === 0 ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>Be the first in the world!</p> : (
                      top99.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', backgroundColor: index % 2 === 0 ? (isDarkMode ? '#1e293b' : '#f8fafc') : 'transparent', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', color: tStyle.text }}>
                          <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: tStyle.subtext, width: '25px', fontSize: '14px' }}>#{index + 1}</span>
                            <span style={{ color: entry.name === playerName ? tStyle.highlight : tStyle.text }}>{entry.name}</span>
                          </span>
                          <span style={{ color: '#22c55e', fontSize: '18px' }}>{entry.score} pts</span>
                        </div>
                      ))
                    )
                  )
                )}
              </div>

              {leaderboardView === 'global' && myBestScore !== null && (
                 <div style={{ padding: '12px', backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9', borderRadius: '8px', border: `1px solid ${tStyle.border}`, marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: tStyle.text, fontWeight: 'bold', fontSize: '14px' }}>Your Rank: <span style={{ color: tStyle.highlight, fontSize: '18px', marginLeft: '5px' }}>{myGlobalRank > 99 ? '99+' : `#${myGlobalRank}`}</span></span>
                    <span style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '16px' }}>{myBestScore} pts</span>
                 </div>
              )}

              <div style={{ display: 'flex', gap: '15px' }}>
                <button onClick={onExit} style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>Quit</button>
                <button onClick={startGame} style={{ flex: 1, padding: '14px', backgroundColor: tStyle.highlight, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', boxShadow: '0 6px 15px rgba(239, 68, 68, 0.4)' }}>Pop Again 🔄</button>
              </div>
            </div>
          </div>
        )}

        {isPlaying && balloons.map((b) => (
          <div key={b.id} style={{ position: 'absolute', left: `${b.left}%`, bottom: `${b.bottom}%`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '55px', height: '65px', backgroundColor: '#ef4444', borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold', fontSize: '28px', fontFamily: "'Courier New', Courier, monospace", boxShadow: 'inset -5px -5px 15px rgba(0,0,0,0.3), inset 5px 5px 10px rgba(255,255,255,0.4)', zIndex: 2 }}>{b.letter}</div>
            <div style={{ width: '0', height: '0', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '10px solid #ef4444', marginTop: '-4px', zIndex: 1 }}></div>
            <div style={{ width: '2px', height: '40px', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)', marginTop: '0px' }}></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// ☄️ METEOR DEFENSE GAME ENGINE (V1 - Mobile Fixed)
// ==========================================
function MeteorGame({ isDarkMode, onExit, arcadeLevel, currentUser, soundEnabled }) {
  const [gameState, setGameState] = useState({ meteors: [], lifelines: 5, score: 0, isPlaying: false, currentInput: "" });
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameOverPhase, setGameOverPhase] = useState('none'); 
  const [playerName, setPlayerName] = useState(currentUser ? currentUser.split('@')[0] : 'Guest');

  // 🚀 Leaderboard States
  const [leaderboardView, setLeaderboardView] = useState('personal'); 
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [isFetchingGlobal, setIsFetchingGlobal] = useState(false);

  // 🚀 Hidden Input Reference for Mobile Keyboard
  const hiddenInputRef = useRef(null);

  const currentLessonData = ARCADE_LESSONS[arcadeLevel] || ARCADE_LESSONS[1];
  const activeLetters = currentLessonData.chars;
  
  const generateAllowedWord = () => {
    const wordLength = Math.floor(Math.random() * 4) + 2; 
    let word = "";
    for (let i = 0; i < wordLength; i++) {
      word += activeLetters[Math.floor(Math.random() * activeLetters.length)];
    }
    return word;
  };

  useEffect(() => {
    const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
    const userKey = currentUser || 'Guest';
    const userDb = db[userKey] || {};
    setLeaderboard(userDb.meteorLeaderboard || []);
    if (currentUser) setPlayerName(currentUser.split('@')[0]);
  }, [currentUser]);

  // 🚀 Word Fire Logic (Used by BOTH Desktop & Mobile)
  const handleFireWord = useCallback((wordToFire) => {
    setGameState(prev => {
      const typedWord = wordToFire.trim();
      if (!typedWord) return { ...prev, currentInput: "" }; 
      const matchIndex = prev.meteors.findIndex(m => m.word.toLowerCase() === typedWord.toLowerCase());
      if (matchIndex !== -1) {
        playSound('explosion', soundEnabled);
        const pointsGained = typedWord.length * 10;
        return { 
          ...prev, 
          score: prev.score + pointsGained, 
          meteors: prev.meteors.filter((_, idx) => idx !== matchIndex), 
          currentInput: "" 
        };
      } else {
        playSound('error', soundEnabled);
        return { ...prev, score: Math.max(0, prev.score - 5), currentInput: "" };
      }
    });
  }, [soundEnabled]);

  const startGame = () => {
    setGameOverPhase('none');
    setGameState({ meteors: [], lifelines: 5, score: 0, isPlaying: true, currentInput: "" });
    setTimeout(() => {
      hiddenInputRef.current?.focus();
    }, 50);
  };

  // 🚀 Meteor Spawner
  useEffect(() => {
    if (!gameState.isPlaying) return;
    const spawner = setInterval(() => {
      setGameState((prev) => {
        if (!prev.isPlaying) return prev;
        const maxMeteors = prev.score >= 300 ? 3 : (prev.score >= 100 ? 2 : 1);
        const numToSpawn = Math.floor(Math.random() * maxMeteors) + 1; 
        const newMeteors = [];
        for (let i = 0; i < numToSpawn; i++) {
          newMeteors.push({ id: Date.now() + Math.random(), word: generateAllowedWord(), left: Math.floor(Math.random() * 70) + 15, top: -10 - (Math.random() * 15) });
        }
        return { ...prev, meteors: [...prev.meteors, ...newMeteors] };
      });
    }, 2000); 
    return () => clearInterval(spawner);
  }, [gameState.isPlaying, activeLetters]);

  // 🚀 Meteor Movement & Impact
  useEffect(() => {
    if (!gameState.isPlaying) return;
    const gameLoop = setInterval(() => {
      setGameState((prev) => {
        if (!prev.isPlaying) return prev;
        let missedCount = 0;
        const updatedMeteors = [];
        const currentSpeed = 0.15 + (prev.score * 0.0003); 
        for (const m of prev.meteors) {
          const newTop = m.top + currentSpeed;
          if (newTop > 100) missedCount += 1; 
          else updatedMeteors.push({ ...m, top: newTop });
        }
        if (missedCount > 0) {
          playSound('error', soundEnabled);
          const newLifelines = prev.lifelines - missedCount;
          if (newLifelines <= 0) {
            setGameOverPhase('nameEntry'); 
            return { ...prev, meteors: updatedMeteors, lifelines: newLifelines, isPlaying: false, currentInput: "" };
          }
          return { ...prev, meteors: updatedMeteors, lifelines: newLifelines };
        }
        return { ...prev, meteors: updatedMeteors };
      });
    }, 16); 
    return () => clearInterval(gameLoop);
  }, [gameState.isPlaying, soundEnabled]);

  // 🚀 Desktop Physical Keyboard Listener
  useEffect(() => {
    if (!gameState.isPlaying) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Backspace') {
        setGameState(prev => ({ ...prev, currentInput: prev.currentInput.slice(0, -1) }));
        return;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault(); 
        handleFireWord(gameState.currentInput);
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        playSound('laser', soundEnabled);
        setGameState(prev => ({ ...prev, currentInput: prev.currentInput + e.key }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState.isPlaying, gameState.currentInput, handleFireWord, soundEnabled]);

  // 🚀 Auto-focus mobile input when game starts
  useEffect(() => {
    if (gameState.isPlaying && hiddenInputRef.current) {
      hiddenInputRef.current.focus();
    }
  }, [gameState.isPlaying]);

  const handleSaveScore = async () => {
    if (gameState.score > 0) {
      const finalScore = gameState.score;
      const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      const userKey = currentUser || 'Guest';
      
      if (!db[userKey]) db[userKey] = { meteorLeaderboard: [] };
      if (!db[userKey].meteorLeaderboard) db[userKey].meteorLeaderboard = [];
      
      const currentBoard = db[userKey].meteorLeaderboard;
      const otherLevelScores = currentBoard.filter(s => s.levelId !== Number(arcadeLevel));
      
      let thisLevelScores = currentBoard.filter(s => s.levelId === Number(arcadeLevel));
      thisLevelScores.push({ name: playerName.trim() || 'Guest', email: currentUser || 'Guest', score: finalScore, levelId: Number(arcadeLevel), date: Date.now() });
      
      thisLevelScores.sort((a, b) => b.score - a.score);
      thisLevelScores = thisLevelScores.slice(0, 10);
      
      const updatedBoard = [...otherLevelScores, ...thisLevelScores];
      db[userKey].meteorLeaderboard = updatedBoard;
      
      localStorage.setItem('typeFasterDB', JSON.stringify(db));
      setLeaderboard(updatedBoard);
      
      try { 
        if (currentUser) {
          await supabase.from('users').update({ progress_data: db[userKey] }).eq('email', currentUser); 
        }
      } catch (err) {}
    }
    setGameOverPhase('leaderboard');
  };

  const fetchGlobalLeaderboard = useCallback(async () => {
    setIsFetchingGlobal(true);
    try {
      const { data } = await supabase.from('users').select('progress_data');
      let allScores = [];

      if (data) {
        data.forEach(user => {
          const userScores = user.progress_data?.meteorLeaderboard || [];
          const levelScores = userScores.filter(s => s.levelId === Number(arcadeLevel));
          allScores.push(...levelScores);
        });
      }

      const localDb = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      if (localDb['Guest'] && localDb['Guest'].meteorLeaderboard) {
        allScores.push(...localDb['Guest'].meteorLeaderboard.filter(s => s.levelId === Number(arcadeLevel)));
      }

      allScores.sort((a, b) => b.score - a.score);

      const uniqueScores = [];
      const seenAccounts = new Set();
      
      for (const s of allScores) {
        const accountId = s.email || s.name; 
        if (!seenAccounts.has(accountId)) {
          uniqueScores.push(s);
          seenAccounts.add(accountId);
        }
      }

      setGlobalLeaderboard(uniqueScores);
    } catch (e) {
      console.error("Error fetching global leaderboard", e);
    }
    setIsFetchingGlobal(false);
  }, [arcadeLevel]);

  useEffect(() => {
    const isBoardOpen = gameOverPhase === 'leaderboard';
    if (isBoardOpen && leaderboardView === 'global') {
      fetchGlobalLeaderboard();
    }
  }, [gameOverPhase, leaderboardView, fetchGlobalLeaderboard]);

  const personalScores = leaderboard.filter(s => s.levelId === Number(arcadeLevel)).sort((a, b) => b.score - a.score).slice(0, 10);
  const top99 = globalLeaderboard.slice(0, 99);
  
  let myGlobalRank = -1;
  let myBestScore = null;
  
  const myIndex = globalLeaderboard.findIndex(s => 
    (currentUser && s.email === currentUser) || 
    (!currentUser && s.name === (playerName.trim() || 'Guest'))
  );
  
  if (myIndex !== -1) {
    myGlobalRank = myIndex + 1;
    myBestScore = globalLeaderboard[myIndex].score;
  }

  const tStyle = {
    bg: isDarkMode ? '#0f172a' : '#f1f5f9', 
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    highlight: '#f97316',                        
    border: isDarkMode ? '#334155' : '#e2e8f0',
    sky: isDarkMode ? '#0b1120' : '#1e1e2f', 
    popBg: isDarkMode ? '#0f172a' : '#ffffff',
    inputBg: isDarkMode ? '#1e293b' : '#f1f5f9',
  };

  const { meteors, lifelines, score, isPlaying, currentInput } = gameState;

  return (
    <div 
      onClick={() => hiddenInputRef.current?.focus()} 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: tStyle.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}
    >
      
      {/* 🚀 BULLETPROOF MOBILE INPUT FOR WORDS & SPACES */}
      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="text"
        value={currentInput}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck="false"
        style={{
          position: 'absolute',
          opacity: 0,
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          cursor: 'default',
          caretColor: 'transparent'
        }}
        onChange={(e) => {
          const val = e.target.value;
          // If the user hit space or enter on mobile, fire the word!
          if (val.endsWith(' ') || val.endsWith('\n')) {
            handleFireWord(val.trim());
          } else {
            playSound('laser', soundEnabled);
            setGameState(prev => ({ ...prev, currentInput: val }));
          }
        }}
      />

      <div style={{ width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'space-between', marginBottom: '20px', marginTop: '10px', zIndex: 2 }}>
        <button onClick={onExit} style={{ padding: '10px 20px', backgroundColor: tStyle.popBg, color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>⬅️ Exit Arcade</button>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: tStyle.text, display: 'flex', gap: '25px', alignItems: 'center' }}>
          <span style={{ fontSize: '15px', color: '#64748b', alignSelf: 'center', marginRight: '10px', borderRight: `2px solid ${tStyle.border}`, paddingRight: '15px' }}>{currentLessonData.title}</span>
          <span>Score: <span style={{color: '#f97316', fontSize: '24px'}}>{score}</span></span> 
          <span>City Shields: <span style={{ fontSize: '22px'}}>{'🛡️'.repeat(Math.max(0, lifelines))}</span></span>
        </div>
      </div>

      <div style={{ position: 'relative', width: '100%', maxWidth: '900px', flexGrow: 1, minHeight: '400px', backgroundColor: tStyle.sky, border: `4px solid ${tStyle.border}`, borderRadius: '24px', overflow: 'hidden', boxShadow: 'inset 0 0 30px rgba(0,0,0,0.5), 0 10px 30px rgba(0,0,0,0.2)', zIndex: 2 }}>
        
        {!isPlaying && gameOverPhase === 'none' && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10, position: 'relative' }}>
            <div style={{ fontSize: '60px', marginBottom: '15px', transform: 'rotate(-45deg)' }}>☄️</div>
            <h2 style={{ color: 'white', fontSize: '40px', margin: '0 0 10px 0', textShadow: '0 4px 8px rgba(0,0,0,0.5)' }}>Meteor Defense</h2>
            <p style={{ color: '#fdba74', fontSize: '18px', marginBottom: '30px' }}>Type full words. Press Space to fire!</p>
            <button onClick={startGame} style={{ padding: '16px 40px', fontSize: '24px', fontWeight: 'bold', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '16px', cursor: 'pointer', boxShadow: '0 8px 20px rgba(249, 115, 22, 0.4)' }}>Start Game ▶️</button>
          </div>
        )}

        {gameOverPhase === 'nameEntry' && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 20, position: 'absolute', top: 0, left: 0, width: '100%', padding: '20px', boxSizing: 'border-box' }}>
            <div style={{ width: '100%', maxWidth: '350px', backgroundColor: tStyle.popBg, borderRadius: '16px', padding: '30px', border: `3px solid ${isDarkMode ? '#ef4444' : '#dc2626'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              <h2 style={{ color: isDarkMode ? '#fca5a5' : '#b91c1c', margin: '0 0 5px 0', fontSize: '28px' }}>City Destroyed!</h2>
              <p style={{ color: tStyle.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '25px' }}>Final Score: <span style={{ color: '#f97316', fontSize: '28px' }}>{score}</span></p>
              <label style={{ alignSelf: 'flex-start', fontSize: '13px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Commander Name</label>
              <input type="text" value={playerName} onChange={(e) => setPlayerName(e.target.value)} maxLength={15} style={{ width: '100%', padding: '15px', borderRadius: '10px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.text, fontWeight: 'bold', fontSize: '18px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box', textAlign: 'center' }} />
              <button onClick={handleSaveScore} style={{ width: '100%', padding: '15px', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)' }}>Save Score</button>
            </div>
          </div>
        )}

        {/* TWO-TAB LEADERBOARD UI */}
        {gameOverPhase === 'leaderboard' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 20, padding: '20px' }}>
            <div style={{ width: '100%', maxWidth: '450px', backgroundColor: tStyle.popBg, borderRadius: '16px', padding: '25px', border: `3px solid ${tStyle.highlight}`, display: 'flex', flexDirection: 'column', maxHeight: '95%', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button onClick={() => setLeaderboardView('personal')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', backgroundColor: leaderboardView === 'personal' ? tStyle.highlight : tStyle.inputBg, color: leaderboardView === 'personal' ? 'white' : tStyle.subtext, transition: '0.2s' }}>
                  👤 Personal (10)
                </button>
                <button onClick={() => setLeaderboardView('global')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', backgroundColor: leaderboardView === 'global' ? tStyle.highlight : tStyle.inputBg, color: leaderboardView === 'global' ? 'white' : tStyle.subtext, transition: '0.2s' }}>
                  🌍 Global (99)
                </button>
              </div>

              <h2 style={{ textAlign: 'center', color: tStyle.highlight, margin: '0 0 15px 0', fontSize: '20px' }}>
                {leaderboardView === 'personal' ? 'Your Best Defenses' : 'World Starfleet'} <br/> 
                <span style={{ fontSize: '14px', color: tStyle.subtext }}>{currentLessonData.title}</span>
              </h2>

              <div style={{ flexGrow: 1, overflowY: 'auto', marginBottom: '15px', paddingRight: '5px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                
                {leaderboardView === 'personal' ? (
                  personalScores.length === 0 ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>No scores for this lesson yet.</p> : (
                    personalScores.map((entry, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', backgroundColor: index % 2 === 0 ? (isDarkMode ? '#1e293b' : '#f8fafc') : 'transparent', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', color: tStyle.text }}>
                        <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <span style={{ color: tStyle.subtext, width: '25px', fontSize: '14px' }}>#{index + 1}</span>
                          <span style={{ color: entry.name === playerName ? tStyle.highlight : tStyle.text }}>{entry.name}</span>
                        </span>
                        <span style={{ color: '#22c55e', fontSize: '18px' }}>{entry.score} pts</span>
                      </div>
                    ))
                  )
                ) : (
                  isFetchingGlobal ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>📡 Fetching scores...</p> : (
                    top99.length === 0 ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>Be the first in the world!</p> : (
                      top99.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', backgroundColor: index % 2 === 0 ? (isDarkMode ? '#1e293b' : '#f8fafc') : 'transparent', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', color: tStyle.text }}>
                          <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: tStyle.subtext, width: '25px', fontSize: '14px' }}>#{index + 1}</span>
                            <span style={{ color: entry.name === playerName ? tStyle.highlight : tStyle.text }}>{entry.name}</span>
                          </span>
                          <span style={{ color: '#22c55e', fontSize: '18px' }}>{entry.score} pts</span>
                        </div>
                      ))
                    )
                  )
                )}
              </div>

              {leaderboardView === 'global' && myBestScore !== null && (
                 <div style={{ padding: '12px', backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9', borderRadius: '8px', border: `1px solid ${tStyle.border}`, marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: tStyle.text, fontWeight: 'bold', fontSize: '14px' }}>Your Rank: <span style={{ color: tStyle.highlight, fontSize: '18px', marginLeft: '5px' }}>{myGlobalRank > 99 ? '99+' : `#${myGlobalRank}`}</span></span>
                    <span style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '16px' }}>{myBestScore} pts</span>
                 </div>
              )}

              <div style={{ display: 'flex', gap: '15px' }}>
                <button onClick={onExit} style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>Quit</button>
                <button onClick={startGame} style={{ flex: 1, padding: '14px', backgroundColor: tStyle.highlight, color: '#000', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', boxShadow: '0 6px 15px rgba(234, 179, 8, 0.4)' }}>Defend Again 🔄</button>
              </div>
            </div>
          </div>
        )}

        {isPlaying && (
          <>
            {meteors.map((m) => (
              <div key={m.id} style={{ position: 'absolute', left: `${m.left}%`, top: `${m.top}%`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '20px', height: '30px', background: 'linear-gradient(to top, #f97316, transparent)', borderTopLeftRadius: '50%', borderTopRightRadius: '50%', marginBottom: '-10px', zIndex: 1, opacity: 0.8 }}></div>
                <div style={{ padding: '8px 12px', backgroundColor: '#475569', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold', fontSize: '18px', fontFamily: "'Courier New', Courier, monospace", boxShadow: '0 0 15px rgba(249, 115, 22, 0.5), inset 2px 2px 5px rgba(255,255,255,0.2)', zIndex: 2, border: '2px solid #1e293b' }}>
                  {m.word}
                </div>
              </div>
            ))}
            <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '300px', backgroundColor: 'rgba(15, 23, 42, 0.8)', padding: '15px', borderRadius: '12px', border: '2px solid #38bdf8', backdropFilter: 'blur(4px)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 5 }}>
              <div style={{ fontSize: '12px', color: '#38bdf8', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Laser Targeting</div>
              <div style={{ width: '100%', height: '40px', backgroundColor: '#020617', borderRadius: '6px', border: '1px solid #1e3a8a', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontSize: '20px', fontFamily: "'Courier New', Courier, monospace", letterSpacing: '2px', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5)' }}>
                {currentInput}
                <span style={{ width: '10px', height: '20px', backgroundColor: '#38bdf8', marginLeft: '5px', animation: 'blink 1s step-end infinite' }}></span>
              </div>
            </div>
            <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
          </>
        )}
      </div>
    </div>
  );
}

// ==========================================
// 🏎️ TYPE RACER GAME ENGINE (V3 - Mobile Fixed)
// ==========================================
function TypeRacerGame({ isDarkMode, onExit, arcadeLevel, currentUser, soundEnabled }) {
  const [gameState, setGameState] = useState({ words: [], currentIndex: 0, currentInput: "", score: 0, timeLeft: 60, timeBonus: 0, isPlaying: false });
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameOverPhase, setGameOverPhase] = useState('none');
  const [playerName, setPlayerName] = useState(currentUser ? currentUser.split('@')[0] : 'Guest');

  // 🚀 Leaderboard States
  const [leaderboardView, setLeaderboardView] = useState('personal'); 
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [isFetchingGlobal, setIsFetchingGlobal] = useState(false);
  const inputRef = useRef(null);

  const currentLessonData = ARCADE_LESSONS[arcadeLevel] || ARCADE_LESSONS[1];
  const activeLetters = currentLessonData.chars;
  const allowCapitals = currentLessonData.caps;

  const generateParagraph = (wordCount) => {
    let generatedWords = [];
    for (let i = 0; i < wordCount; i++) {
      let wordLen = Math.floor(Math.random() * 5) + 2; 
      let word = "";
      for (let j = 0; j < wordLen; j++) {
        let char = activeLetters[Math.floor(Math.random() * activeLetters.length)];
        if (allowCapitals && Math.random() > 0.8) char = char.toUpperCase();
        word += char;
      }
      generatedWords.push(word);
    }
    return generatedWords;
  };

  useEffect(() => {
    const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
    const userKey = currentUser || 'Guest';
    const userDb = db[userKey] || {};
    setLeaderboard(userDb.racerLeaderboard || []);
    if (currentUser) setPlayerName(currentUser.split('@')[0]);
  }, [currentUser]);

  const startGame = () => {
    setGameOverPhase('none');
    setGameState({ words: generateParagraph(40), currentIndex: 0, currentInput: "", score: 0, timeLeft: 60, timeBonus: 0, isPlaying: true });
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  useEffect(() => {
    if (gameState.isPlaying && inputRef.current) {
      inputRef.current.focus();
    }
  }, [gameState.isPlaying]);

  useEffect(() => {
    if (!gameState.isPlaying) return;
    const timer = setInterval(() => {
      setGameState((prev) => {
        if (prev.timeLeft <= 1) {
          setGameOverPhase('nameEntry'); 
          return { ...prev, timeLeft: 0, isPlaying: false, currentInput: "" };
        }
        return { ...prev, timeLeft: prev.timeLeft - 1 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState.isPlaying]);

  const handleInputChange = (e) => {
    if (!gameState.isPlaying) return;
    const val = e.target.value;
    const targetWord = gameState.words[gameState.currentIndex];

    if (val.endsWith(' ') || val.endsWith('\n')) {
      const typedWord = val.trim();
      if (typedWord === targetWord) {
        playSound('engine', soundEnabled);
        setGameState((prev) => {
          const newIndex = prev.currentIndex + 1;
          const pointsGained = targetWord.length * 10;
          if (newIndex >= prev.words.length) {
            setGameOverPhase('nameEntry');
            const bonus = prev.timeLeft * 20; 
            return { ...prev, currentIndex: newIndex, score: prev.score + pointsGained + bonus, timeBonus: bonus, isPlaying: false, currentInput: "" };
          }
          return { ...prev, currentIndex: newIndex, score: prev.score + pointsGained, currentInput: "" };
        });
      } else {
        playSound('screech', soundEnabled);
        setGameState(prev => ({ ...prev, currentInput: val }));
      }
    } else {
      const isErrorNow = val.length > 0 && !targetWord.startsWith(val);
      const wasErrorBefore = gameState.currentInput.length > 0 && !targetWord.startsWith(gameState.currentInput);

      if (isErrorNow && !wasErrorBefore) {
        playSound('screech', soundEnabled);
      }

      setGameState(prev => ({ ...prev, currentInput: val }));
    }
  };

  const handleSaveScore = async () => {
    if (gameState.score > 0) {
      const finalScore = gameState.score;
      const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      const userKey = currentUser || 'Guest';
      
      if (!db[userKey]) db[userKey] = { racerLeaderboard: [] };
      if (!db[userKey].racerLeaderboard) db[userKey].racerLeaderboard = [];
      
      const currentBoard = db[userKey].racerLeaderboard;
      const otherLevelScores = currentBoard.filter(s => s.levelId !== Number(arcadeLevel));
      
      let thisLevelScores = currentBoard.filter(s => s.levelId === Number(arcadeLevel));
      thisLevelScores.push({ name: playerName.trim() || 'Guest', email: currentUser || 'Guest', score: finalScore, levelId: Number(arcadeLevel), date: Date.now() });
      
      thisLevelScores.sort((a, b) => b.score - a.score);
      thisLevelScores = thisLevelScores.slice(0, 10);
      
      const updatedBoard = [...otherLevelScores, ...thisLevelScores];
      db[userKey].racerLeaderboard = updatedBoard;
      
      localStorage.setItem('typeFasterDB', JSON.stringify(db));
      setLeaderboard(updatedBoard);
      
      try { 
        if (currentUser) {
          await supabase.from('users').update({ progress_data: db[userKey] }).eq('email', currentUser); 
        }
      } catch (err) {}
    }
    setGameOverPhase('leaderboard');
  };

  const fetchGlobalLeaderboard = useCallback(async () => {
    setIsFetchingGlobal(true);
    try {
      const { data } = await supabase.from('users').select('progress_data');
      let allScores = [];

      if (data) {
        data.forEach(user => {
          const userScores = user.progress_data?.racerLeaderboard || [];
          const levelScores = userScores.filter(s => s.levelId === Number(arcadeLevel));
          allScores.push(...levelScores);
        });
      }

      const localDb = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      if (localDb['Guest'] && localDb['Guest'].racerLeaderboard) {
        allScores.push(...localDb['Guest'].racerLeaderboard.filter(s => s.levelId === Number(arcadeLevel)));
      }

      allScores.sort((a, b) => b.score - a.score);

      const uniqueScores = [];
      const seenAccounts = new Set();
      
      for (const s of allScores) {
        const accountId = s.email || s.name; 
        if (!seenAccounts.has(accountId)) {
          uniqueScores.push(s);
          seenAccounts.add(accountId);
        }
      }

      setGlobalLeaderboard(uniqueScores);
    } catch (e) {
      console.error("Error fetching global leaderboard", e);
    }
    setIsFetchingGlobal(false);
  }, [arcadeLevel]);

  useEffect(() => {
    const isBoardOpen = gameOverPhase === 'leaderboard';
    if (isBoardOpen && leaderboardView === 'global') {
      fetchGlobalLeaderboard();
    }
  }, [gameOverPhase, leaderboardView, fetchGlobalLeaderboard]);

  const personalScores = leaderboard.filter(s => s.levelId === Number(arcadeLevel)).sort((a, b) => b.score - a.score).slice(0, 10);
  const top99 = globalLeaderboard.slice(0, 99);
  
  let myGlobalRank = -1;
  let myBestScore = null;
  
  const myIndex = globalLeaderboard.findIndex(s => 
    (currentUser && s.email === currentUser) || 
    (!currentUser && s.name === (playerName.trim() || 'Guest'))
  );
  
  if (myIndex !== -1) {
    myGlobalRank = myIndex + 1;
    myBestScore = globalLeaderboard[myIndex].score;
  }

  const tStyle = {
    bg: isDarkMode ? '#0f172a' : '#f1f5f9',
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    highlight: '#a855f7',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    sky: isDarkMode ? '#1e1b4b' : '#f3e8ff', 
    popBg: isDarkMode ? '#0f172a' : '#ffffff',
    inputBg: isDarkMode ? '#1e293b' : '#ffffff',
  };

  const { words, currentIndex, currentInput, score, timeLeft, timeBonus, isPlaying } = gameState;
  const targetWord = words[currentIndex] || "";
  const isError = currentInput.trim().length > 0 && !targetWord.startsWith(currentInput.trim());
  const progressPercent = words.length > 0 ? (currentIndex / words.length) * 100 : 0;

  return (
    <div 
      onClick={() => inputRef.current?.focus()} 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: tStyle.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}
    >
      <div style={{ width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'space-between', marginBottom: '20px', marginTop: '10px' }}>
        <button onClick={onExit} style={{ padding: '10px 20px', backgroundColor: tStyle.popBg, color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>⬅️ Exit Arcade</button>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: tStyle.text, display: 'flex', gap: '25px', alignItems: 'center' }}>
          <span style={{ fontSize: '15px', color: '#64748b', alignSelf: 'center', marginRight: '10px', borderRight: `2px solid ${tStyle.border}`, paddingRight: '15px' }}>{currentLessonData.title}</span>
          <span>Score: <span style={{color: '#a855f7', fontSize: '24px'}}>{score}</span></span> 
          <span>Time: <span style={{ fontSize: '24px', color: timeLeft <= 10 ? '#ef4444' : tStyle.text }}>0:{String(timeLeft).padStart(2, '0')}</span></span>
        </div>
      </div>

      <div style={{ position: 'relative', width: '100%', maxWidth: '900px', flexGrow: 1, minHeight: '400px', backgroundColor: tStyle.sky, border: `4px solid ${tStyle.border}`, borderRadius: '24px', overflow: 'hidden', boxShadow: 'inset 0 0 30px rgba(0,0,0,0.2), 0 10px 30px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
        {!isPlaying && gameOverPhase === 'none' && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10 }}>
            <div style={{ fontSize: '60px', marginBottom: '15px' }}>🏎️</div>
            <h2 style={{ color: 'white', fontSize: '40px', margin: '0 0 10px 0', textShadow: '0 4px 8px rgba(0,0,0,0.5)' }}>Type Racer</h2>
            <p style={{ color: '#d8b4fe', fontSize: '18px', marginBottom: '30px', maxWidth: '400px', textAlign: 'center', lineHeight: '1.5' }}>Type the words to drive your car. Finish before 60 seconds runs out to get a time bonus!</p>
            <button onClick={startGame} style={{ padding: '16px 40px', fontSize: '24px', fontWeight: 'bold', backgroundColor: '#a855f7', color: 'white', border: 'none', borderRadius: '16px', cursor: 'pointer', boxShadow: '0 8px 20px rgba(168, 85, 247, 0.4)' }}>Start Race ▶️</button>
          </div>
        )}

        {gameOverPhase === 'nameEntry' && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 20, padding: '20px', boxSizing: 'border-box' }}>
            <div style={{ width: '100%', maxWidth: '350px', backgroundColor: tStyle.popBg, borderRadius: '16px', padding: '30px', border: `3px solid #a855f7`, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              <h2 style={{ color: '#a855f7', margin: '0 0 15px 0', fontSize: '28px' }}>Race Finished!</h2>
              <div style={{ width: '100%', backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: tStyle.subtext, fontSize: '14px', marginBottom: '8px' }}>
                  <span>Base Score:</span>
                  <span style={{ fontWeight: 'bold', color: tStyle.text }}>{score - timeBonus}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: tStyle.subtext, fontSize: '14px', marginBottom: '12px' }}>
                  <span>Time Bonus ({timeLeft}s):</span>
                  <span style={{ fontWeight: 'bold', color: '#22c55e' }}>+{timeBonus}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${tStyle.border}`, paddingTop: '10px', fontSize: '18px', fontWeight: 'bold', color: tStyle.text }}>
                  <span>Total:</span>
                  <span style={{ color: '#0ea5e9' }}>{score}</span>
                </div>
              </div>
              <label style={{ alignSelf: 'flex-start', fontSize: '13px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Driver Name</label>
              <input type="text" value={playerName} onChange={(e) => setPlayerName(e.target.value)} maxLength={15} style={{ width: '100%', padding: '15px', borderRadius: '10px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.text, fontWeight: 'bold', fontSize: '18px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box', textAlign: 'center' }} />
              <button onClick={handleSaveScore} style={{ width: '100%', padding: '15px', backgroundColor: '#a855f7', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', boxShadow: '0 4px 15px rgba(168, 85, 247, 0.4)' }}>Save Score</button>
            </div>
          </div>
        )}

        {/* TWO-TAB LEADERBOARD UI */}
        {gameOverPhase === 'leaderboard' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 20, padding: '20px' }}>
            <div style={{ width: '100%', maxWidth: '450px', backgroundColor: tStyle.popBg, borderRadius: '16px', padding: '25px', border: `3px solid ${tStyle.highlight}`, display: 'flex', flexDirection: 'column', maxHeight: '95%', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button onClick={() => setLeaderboardView('personal')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', backgroundColor: leaderboardView === 'personal' ? tStyle.highlight : tStyle.inputBg, color: leaderboardView === 'personal' ? 'white' : tStyle.subtext, transition: '0.2s' }}>
                  👤 Personal (10)
                </button>
                <button onClick={() => setLeaderboardView('global')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', backgroundColor: leaderboardView === 'global' ? tStyle.highlight : tStyle.inputBg, color: leaderboardView === 'global' ? 'white' : tStyle.subtext, transition: '0.2s' }}>
                  🌍 Global (99)
                </button>
              </div>

              <h2 style={{ textAlign: 'center', color: '#a855f7', margin: '0 0 15px 0', fontSize: '20px' }}>
                {leaderboardView === 'personal' ? 'Your Best Races' : 'World Fastest Drivers'} <br/> 
                <span style={{ fontSize: '14px', color: tStyle.subtext }}>{currentLessonData.title}</span>
              </h2>

              <div style={{ flexGrow: 1, overflowY: 'auto', marginBottom: '15px', paddingRight: '5px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {leaderboardView === 'personal' ? (
                  personalScores.length === 0 ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>No scores for this lesson yet.</p> : (
                    personalScores.map((entry, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', backgroundColor: index % 2 === 0 ? (isDarkMode ? '#1e293b' : '#f8fafc') : 'transparent', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', color: tStyle.text }}>
                        <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <span style={{ color: tStyle.subtext, width: '25px', fontSize: '14px' }}>#{index + 1}</span>
                          <span style={{ color: entry.name === playerName ? tStyle.highlight : tStyle.text }}>{entry.name}</span>
                        </span>
                        <span style={{ color: '#22c55e', fontSize: '18px' }}>{entry.score} pts</span>
                      </div>
                    ))
                  )
                ) : (
                  isFetchingGlobal ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>📡 Fetching scores...</p> : (
                    top99.length === 0 ? <p style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '20px' }}>Be the first in the world!</p> : (
                      top99.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', backgroundColor: index % 2 === 0 ? (isDarkMode ? '#1e293b' : '#f8fafc') : 'transparent', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', color: tStyle.text }}>
                          <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: tStyle.subtext, width: '25px', fontSize: '14px' }}>#{index + 1}</span>
                            <span style={{ color: entry.name === playerName ? tStyle.highlight : tStyle.text }}>{entry.name}</span>
                          </span>
                          <span style={{ color: '#22c55e', fontSize: '18px' }}>{entry.score} pts</span>
                        </div>
                      ))
                    )
                  )
                )}
              </div>

              {leaderboardView === 'global' && myBestScore !== null && (
                 <div style={{ padding: '12px', backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9', borderRadius: '8px', border: `1px solid ${tStyle.border}`, marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: tStyle.text, fontWeight: 'bold', fontSize: '14px' }}>Your Rank: <span style={{ color: tStyle.highlight, fontSize: '18px', marginLeft: '5px' }}>{myGlobalRank > 99 ? '99+' : `#${myGlobalRank}`}</span></span>
                    <span style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '16px' }}>{myBestScore} pts</span>
                 </div>
              )}

              <div style={{ display: 'flex', gap: '15px' }}>
                <button onClick={onExit} style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>Quit</button>
                <button onClick={startGame} style={{ flex: 1, padding: '14px', backgroundColor: '#a855f7', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', boxShadow: '0 6px 15px rgba(168, 85, 247, 0.4)' }}>Race Again 🔄</button>
              </div>
            </div>
          </div>
        )}

        {isPlaying && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '40px', boxSizing: 'border-box' }}>
            <div style={{ position: 'relative', width: '100%', height: '80px', borderBottom: `4px dashed ${isDarkMode ? '#334155' : '#cbd5e1'}`, marginBottom: '40px' }}>
              <div style={{ position: 'absolute', left: '-10px', bottom: '-10px', fontSize: '45px', zIndex: 1 }}>🏁</div>
              <div style={{ position: 'absolute', right: `${progressPercent}%`, bottom: '-15px', fontSize: '50px', transition: 'right 0.3s ease-out', transform: 'translateX(50%)', zIndex: 2 }}>🏎️</div>
            </div>
            <div style={{ width: '100%', flexGrow: 1, backgroundColor: tStyle.inputBg, border: `2px solid ${tStyle.border}`, borderRadius: '12px', padding: '20px', fontSize: '24px', fontFamily: "'Courier New', Courier, monospace", lineHeight: '1.8', color: tStyle.subtext, overflowY: 'auto', marginBottom: '20px', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.05)' }}>
              {words.map((word, idx) => {
                if (idx < currentIndex) return <span key={idx} style={{ color: '#22c55e' }}>{word} </span>;
                if (idx === currentIndex) return <span key={idx} style={{ backgroundColor: isError ? '#fecaca' : '#fef08a', color: isError ? '#b91c1c' : '#854d0e', borderRadius: '4px', padding: '2px 4px', margin: '0 -4px' }}>{word} </span>;
                return <span key={idx} style={{ color: tStyle.text }}>{word} </span>;
              })}
            </div>
            <input 
              ref={inputRef}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck="false"
              value={currentInput} 
              onChange={handleInputChange} 
              onBlur={() => { if (gameState.isPlaying) inputRef.current?.focus(); }}
              placeholder="Type the highlighted word and press Space..."
              onPaste={(e) => e.preventDefault()} 
              onCopy={(e) => e.preventDefault()} 
              onDrop={(e) => e.preventDefault()} 
              style={{ width: '100%', padding: '20px', fontSize: '24px', borderRadius: '12px', border: `3px solid ${isError ? '#ef4444' : '#a855f7'}`, backgroundColor: isError ? (isDarkMode ? '#450a0a' : '#fef2f2') : tStyle.inputBg, color: tStyle.text, outline: 'none', fontFamily: "'Courier New', Courier, monospace", textAlign: 'center', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// 💡 TIPS & FAQS PAGE COMPONENT
// ==========================================
function TipsAndFaqTab({ isDarkMode, isMobile }) {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const tStyle = {
    bg: isDarkMode ? '#020617' : '#f1f5f9',
    colorText: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    itemBg: isDarkMode ? '#1e293b' : '#ffffff',
    highlightBg: isDarkMode ? '#0c4a6e' : '#e0f2fe',
    highlightText: isDarkMode ? '#38bdf8' : '#0284c7',
  };

  const faqs = [
    { q: "How do I type special characters and conjuncts in Kruti Dev?", a: "Official government typing exams require you to use specific 'Alt Codes' for complex characters (like प्र or क्र) instead of using visual multi-key shortcuts. To type these, hold down the Alt key, type the 4-digit code (e.g., 0216) on your Numpad, and release the Alt key. You can view our complete Alt Code Cheat Sheet directly inside the exam simulator!" },
    { q: "Do I need a dedicated Numeric Keypad (Numpad) to type Alt Codes?", a: "In the actual exam hall (like TCS iON centers), yes, you will use a standard desktop keyboard with a Numpad. However, My Typing Coach features a custom-built Alt-Code Emulator. This means you can practice your Alt Codes using any laptop keyboard, even if it only has numbers across the top row!" },
    { q: "Why was my word marked wrong even though it looks visually correct?", a: "Government exam software does not have 'eyes'—it reads the hidden computer code behind the letters. If you use a shortcut to combine two letters visually instead of using the required, official single-character Alt Code, the strict evaluation engine will mark it as a mismatch. Our platform uses this exact same strict evaluation to ensure you are 100% prepared." },
    { q: "Are Mangal (InScript / Remington Gail) fonts different from Kruti Dev?", a: "Yes! Kruti Dev is a legacy font that relies heavily on Alt Codes. Mangal is a modern Unicode font. If you are typing in Mangal, you do NOT need to use Alt Codes for Hindi letters. Instead, you simply use the Halant (्) key to combine base letters, and the computer automatically generates the correct shapes." },
    { q: "How is Net WPM calculated on this platform?", a: "Gross WPM tracks your total typing raw speed. Net WPM subtracts error penalties. We calculate this by taking your total standard five-character key components typed, subtracting the errors based on selected evaluation rules (like SSC Half/Full mistakes), and dividing by the total minutes taken." },
    { q: "Why are my backspace keys locked in some typing tests?", a: "This occurs when practicing under strict Indian Exam mock presets. Examinations like SSC CHSL/CGL or state recruitment tests have explicit guidelines that restrict full paragraph corrections. We simulate these specific software restrictions perfectly to ensure your test-day success." },
    { q: "What constitutes a 'Full Mistake' versus a 'Half Mistake'?", a: "We implement standard competitive criteria guidelines. Omission of words, substitution of words, or incorrect spellings count as Full Mistakes. Wrong capitalization or improper punctuation spacing issues are evaluated as Half Mistakes under applicable exam parameters." },
    { q: "Can I save my speed metrics history across multiple devices?", a: "Absolutely. If you are signed into your account, your statistics, history, daily graphs, unlocked badges, and test logs sync smoothly through our server database. You can track your daily progress anywhere." },
    { q: "How long should I practice daily to see noticeable results?", a: "Consistency is far better than marathon sessions. We highly recommend practicing on our platform for 20 to 30 minutes daily divided across 2-3 focused sessions, ensuring clean breaks to maintain sharp finger precision." },
    { q: "Can I use this software on a mobile phone or tablet?", a: "Yes! While you can read tips and view analytics anywhere, you must connect a physical external hardware keyboard via an OTG cable adapter to your smartphone/tablet to practice typing tests productively." }
  ];

  return (
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '30px', padding: '10px' }}>
      {/* HEADER SECTION */}
      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '8px', color: tStyle.colorText }}>
          Master Your Typing Skills
        </h2>
        <p style={{ color: tStyle.subtext, fontSize: '16px', margin: 0 }}>
          Expert guidelines, exam strategies, and frequently asked questions to boost your WPM.
        </p>
      </div>

      {/* MASTER TYPING TIPS SECTION */}
      <div>
        <h3 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '15px', color: '#0ea5e9', display: 'flex', alignItems: 'center', gap: '10px' }}>
          🚀 Pro Training Tips
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          
          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>1. Accuracy Comes Before Speed</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Never rush your fingers to achieve high speed. Focus entirely on hitting a 98% accuracy rate first. Speed is a natural byproduct of automated muscle memory built through flawless accuracy.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>2. Maintain the Home Row Anchor</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Always return your fingers to the home row positions (ASDF - JKL;). Use the tactile physical bumps on the 'F' and 'J' keys to re-center your hands without looking down at the keyboard.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>3. Look Ahead at the Next Word</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Do not look at the letter you are currently pressing. Train your eyes to read one or two words ahead of what your fingers are actively typing to create a seamless, continuous flow.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>4. Master the Hindi Alt Codes</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>For competitive Indian typing exams using KrutiDev or Remington Gail, practice special character Alt combinations daily. Missing a complex conjunct character can ruin your overall accuracy metrics instantly.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>5. Adjust to Backspace Restrictions</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Many real-world evaluations completely lock the backspace key or penalize its use. Use our custom settings panel to disable backspaces entirely during practice runs to build perfect precision habits.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>6. Keep Your Wrists Elevated</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Never rest your wrists flat against the desk or laptop edge while typing. Keeping your wrists hover-parallel helps prevent fatigue and allows your fingers to strike keys cleanly from above.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>7. Maintain a Smooth Rhythm</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Typing fast in quick bursts and stalling on difficult words wastes energy. Slow down slightly to match a stable rhythm where the keystrokes sound steady and uniform like a metronome.</p>
          </div>

          <div style={{ background: tStyle.itemBg, padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}` }}>
            <h4 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#f59e0b' }}>8. Leverage Exam Simulation Modes</h4>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>Switch directly to our Indian Exams panel to test under identical software conditions as SSC, State High Courts, and railways. Getting used to text layouts reduces test-day performance anxiety.</p>
          </div>

        </div>
      </div>

      <hr style={{ border: 'none', height: '1px', background: tStyle.border, margin: '10px 0' }} />

      {/* FREQUENTLY ASKED QUESTIONS ACCORDION */}
      <div>
        <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          ❓ Frequently Asked Questions
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {faqs.map((faq, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div 
                key={index} 
                style={{ 
                  backgroundColor: tStyle.itemBg, 
                  borderRadius: '12px', 
                  border: `1px solid ${isOpen ? '#8b5cf6' : tStyle.border}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <button
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                  style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: isOpen ? '#8b5cf6' : tStyle.colorText, fontWeight: '700', fontSize: '15px'
                  }}
                >
                  <span>{faq.q}</span>
                  <span style={{ fontSize: '20px', fontWeight: 'normal', transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>+</span>
                </button>

                <div style={{ maxHeight: isOpen ? '500px' : '0px', opacity: isOpen ? 1 : 0, transition: 'max-height 0.4s ease, opacity 0.3s ease', backgroundColor: isOpen ? (isDarkMode ? '#1e1b4b' : '#f3e8ff') : 'transparent' }}>
                  <div style={{ padding: '0 20px 20px 20px', fontSize: '14px', lineHeight: '1.6', color: tStyle.subtext }}>
                    {faq.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// 🏠 HOME PAGE COMPONENT
// ==========================================
function HomePageTab({ isDarkMode, isMobile, setMenuTab }) {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [workflowLoaded, setWorkflowLoaded] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(1);

  const tStyle = {
    colorText: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    border: isDarkMode ? '#1e293b' : '#e2e8f0',
    itemBg: isDarkMode ? '#1e293b' : '#f8fafc',
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ duration: 0.8 }} 
      style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%', gap: '60px', paddingBottom: '40px' }}
    >
      
      {/* 1. HERO SECTION */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', alignItems: 'center', justifyContent: 'space-between', gap: '40px', minHeight: '70vh' }}>
        
        {/* Left Side: Staggered Text Reveal */}
        <motion.div 
          initial="hidden" 
          animate="visible" 
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.3 } } 
          }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start', textAlign: isMobile ? 'center' : 'left' }}
        >
          <motion.div 
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }}
            style={{ 
              display: 'inline-block', 
              padding: '6px 12px', 
              backgroundColor: isDarkMode ? '#1e3a8a' : '#f0f9ff',
              color: isDarkMode ? '#60a5fa' : '#0369a1',
              borderRadius: '20px', 
              fontWeight: 'bold', 
              fontSize: '13px', 
              marginBottom: '20px', 
              border: `1px solid ${isDarkMode ? '#1d4ed8' : '#bae6fd'}` 
            }}
          >
            🚀 Cloud-Synced Typing & Exam Engine
          </motion.div>
          
          <motion.h1 
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }}
            style={{ fontSize: isMobile ? '40px' : '56px', color: tStyle.colorText, margin: '0 0 20px 0', letterSpacing: '-1px', lineHeight: '1.1', fontWeight: '900' }}
          >
            Learn to Type.<br />
            <span style={{ color: '#0284c7' }}>Conquer the Cutoff.</span>
          </motion.h1>
          
          <motion.p 
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }}
            style={{ fontSize: '18px', color: tStyle.subtext, maxWidth: '500px', lineHeight: '1.6', marginBottom: '40px' }}
          >
            Global platforms teach typing. Local sites only test you. My Typing Coach is the only platform that gives you a structured 12-lesson curriculum, built-in Alt-Code emulators, and exact government exam simulations.
          </motion.p>
          
          <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }} style={{ display: 'flex', gap: '25px', alignItems: 'center', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMenuTab("learning")} 
              style={{ 
                padding: '16px 32px', 
                backgroundColor: '#0369a1', /* 👈 CHANGED */
                color: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                fontSize: '18px', 
                fontWeight: 'bold', 
                cursor: 'pointer', 
                boxShadow: '0 8px 25px rgba(3, 105, 161, 0.4)' /* 👈 CHANGED */
              }}
            >
              Start 12-Lesson Course
            </motion.button>
            
            <motion.span 
              whileHover={{ scale: 1.05, color: '#38bdf8' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setMenuTab("exams"); window.scrollTo(0,0); }} 
              style={{ fontSize: '18px', fontWeight: 'bold', color: tStyle.colorText, textDecoration: 'underline', textUnderlineOffset: '4px', cursor: 'pointer', transition: 'color 0.2s' }}
            >
              Explore all exams
            </motion.span>
            
            <motion.button 
              whileHover={{ scale: 1.05, backgroundColor: tStyle.itemBg }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMenuTab("demo-test")}
              style={{ padding: '16px 32px', backgroundColor: 'transparent', color: tStyle.colorText, border: `2px solid ${tStyle.border}`, borderRadius: '12px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Take a Demo Test
            </motion.button>
          </motion.div>
        </motion.div>
        
        {/* Right Side: Animated Image */}
        <motion.div 
          initial={{ x: 80, opacity: 0 }} 
          animate={heroLoaded ? { x: 0, opacity: 1 } : { x: 80, opacity: 0 }}   
          transition={{ duration: 1.2, ease: "easeOut" }} 
          style={{ flex: 1, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
        >
          <img 
            src={isDarkMode ? "/hero-dark.webp" : "/hero-light.webp"} 
            alt="My Typing Coach Illustration"
            onLoad={() => setHeroLoaded(true)}
            fetchpriority="high"
            width="500" 
            height="500" 
            style={{
              width: '100%', maxWidth: '550px', height: 'auto', objectFit: 'contain',
              filter: isDarkMode ? 'drop-shadow(0 20px 40px rgba(0,0,0,0.6))' : 'drop-shadow(0 20px 40px rgba(0,0,0,0.15))'
            }}
          />
        </motion.div>
      </div>

      {/* 2. UNIQUE BRAND FEATURES SECTION */}
      <div style={{ marginTop: '80px', marginBottom: '40px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '60px', alignItems: 'flex-start' }}>
        
        {/* LEFT SIDE: Accordions */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }} 
          whileInView={{ opacity: 1, x: 0 }} 
          viewport={{ once: true, margin: "-50px" }} 
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Platform Advantage</div>
          <h2 style={{ fontSize: isMobile ? '36px' : '48px', color: tStyle.colorText, marginBottom: '20px', lineHeight: '1.2', fontWeight: '900' }}>
            Why choose <br/><span style={{ color: '#0ea5e9' }}>My Typing Coach?</span>
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', minHeight: '380px' }}>
            
            {/* Accordion 1 */}
            <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div onClick={() => setActiveAccordion(1)} style={{ padding: '20px', fontSize: '16px', fontWeight: 'bold', color: tStyle.colorText, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '20px' }}>☁️</span> Cloud-Synced Progress
              </div>
              <motion.div initial={false} animate={{ height: activeAccordion === 1 ? 'auto' : 0, opacity: activeAccordion === 1 ? 1 : 0 }} style={{ overflow: 'hidden' }}>
                <div style={{ padding: '0 20px 20px 55px', color: tStyle.subtext, fontSize: '14px', lineHeight: '1.6' }}>
                  No more losing data. Create an account and your typing speeds, completed lessons, and premium unlocks instantly sync across all your devices via our secure Supabase backend.
                </div>
              </motion.div>
            </div>

            {/* Accordion 2 */}
            <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div onClick={() => setActiveAccordion(2)} style={{ padding: '20px', fontSize: '16px', fontWeight: 'bold', color: tStyle.colorText, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '20px' }}>⌨️</span> Universal Alt-Code Emulator
              </div>
              <motion.div initial={false} animate={{ height: activeAccordion === 2 ? 'auto' : 0, opacity: activeAccordion === 2 ? 1 : 0 }} style={{ overflow: 'hidden' }}>
                <div style={{ padding: '0 20px 20px 55px', color: tStyle.subtext, fontSize: '14px', lineHeight: '1.6' }}>
                  Practicing Kruti Dev on a laptop? Our proprietary software emulator allows you to use standard top-row number keys to generate complex Alt-Code conjuncts. No external Numpad required!
                </div>
              </motion.div>
            </div>

            {/* Accordion 3 */}
            <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div onClick={() => setActiveAccordion(3)} style={{ padding: '20px', fontSize: '16px', fontWeight: 'bold', color: tStyle.colorText, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '20px' }}>⚖️</span> Real Exam Analytics
              </div>
              <motion.div initial={false} animate={{ height: activeAccordion === 3 ? 'auto' : 0, opacity: activeAccordion === 3 ? 1 : 0 }} style={{ overflow: 'hidden' }}>
                <div style={{ padding: '0 20px 20px 55px', color: tStyle.subtext, fontSize: '14px', lineHeight: '1.6' }}>
                  We don't use standard formulas for government exams. We penalize omissions as Full Mistakes and punctuation errors as Half Mistakes, matching official SSC and UPSSSC guidelines perfectly.
                </div>
              </motion.div>
            </div>

          </div>
        </motion.div>

        {/* RIGHT SIDE: Mosaic Feature Cards */}
        <div style={{ flex: 1.2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', width: '100%', position: 'relative' }}>
          
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 0 }}>
             <div style={{ backgroundImage: `radial-gradient(${isDarkMode ? '#38bdf8' : '#0ea5e9'} 2px, transparent 2px)`, backgroundSize: '20px 20px', width: '140px', height: '140px', opacity: 0.3 }}></div>
          </div>

          {/* Top Left */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }} whileHover={{ y: -5 }} 
            style={{ backgroundColor: tStyle.itemBg, borderTop: `4px solid #0ea5e9`, borderBottom: `1px solid ${tStyle.border}`, borderLeft: `1px solid ${tStyle.border}`, borderRight: `1px solid ${tStyle.border}`, borderRadius: '12px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', zIndex: 1 }}
          >
            <h3 style={{ fontSize: '18px', color: isDarkMode ? '#0ea5e9' : '#0369a1', marginBottom: '10px' }}>Official Interface</h3>
            <p style={{ color: tStyle.subtext, lineHeight: '1.6', fontSize: '14px', margin: 0 }}>Experience the exact split-screen layout and restricted backspace controls before exam day.</p>
          </motion.div>

          {/* Top Right */}
          <motion.div 
            initial={{ opacity: 0, y: -40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }} whileHover={{ y: -5 }} 
            style={{ backgroundColor: tStyle.itemBg, borderTop: `4px solid #10b981`, borderBottom: `1px solid ${tStyle.border}`, borderLeft: `1px solid ${tStyle.border}`, borderRight: `1px solid ${tStyle.border}`, borderRadius: '12px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', zIndex: 1 }}
          >
            <h3 style={{ fontSize: '18px', color: isDarkMode ? '#10b981' : '#047857', marginBottom: '10px' }}>8 Arcade Games</h3>
            <p style={{ color: tStyle.subtext, lineHeight: '1.6', fontSize: '14px', margin: 0 }}>Beat burnout. Train your speed and peripheral vision with gamified mechanics.</p>
          </motion.div>

          {/* Bottom Left */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }} whileHover={{ y: -5 }} 
            style={{ backgroundColor: tStyle.itemBg, borderTop: `4px solid #eab308`, borderBottom: `1px solid ${tStyle.border}`, borderLeft: `1px solid ${tStyle.border}`, borderRight: `1px solid ${tStyle.border}`, borderRadius: '12px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', zIndex: 1 }}
          >
            <h3 style={{ fontSize: '18px', color: isDarkMode ? '#eab308' : '#b45309', marginBottom: '10px' }}>Endurance Mode</h3>
            <p style={{ color: tStyle.subtext, lineHeight: '1.6', fontSize: '14px', margin: 0 }}>Build stamina with full 10-minute uninterrupted PYQ paragraph drills.</p>
          </motion.div>

          {/* Bottom Right */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }} whileHover={{ y: -5 }} 
            style={{ backgroundColor: tStyle.itemBg, borderTop: `4px solid #a855f7`, borderBottom: `1px solid ${tStyle.border}`, borderLeft: `1px solid ${tStyle.border}`, borderRight: `1px solid ${tStyle.border}`, borderRadius: '12px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', zIndex: 1 }}
          >
            <h3 style={{ fontSize: '18px', color: isDarkMode ? '#a855f7' : '#7e22ce', marginBottom: '10px' }}>Font Mastery</h3>
            <p style={{ color: tStyle.subtext, lineHeight: '1.6', fontSize: '14px', margin: 0 }}>Seamlessly practice in English, Mangal InScript, and Kruti Dev instantly.</p>
          </motion.div>

        </div>
      </div>

      {/* 3. BILINGUAL MOCK HIGHLIGHT SECTION */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        whileInView={{ opacity: 1, scale: 1 }} 
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 1.0, ease: "easeOut" }} 
        style={{ 
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', 
          backgroundColor: isDarkMode ? '#1e1b4b' : '#f3e8ff', borderRadius: '24px', 
          padding: isMobile ? '30px' : '50px', gap: '40px', 
          border: `1px solid ${isDarkMode ? '#312e81' : '#e9d5ff'}`,
          boxShadow: isDarkMode ? '0 20px 40px rgba(0,0,0,0.3)' : '0 20px 40px rgba(168, 85, 247, 0.05)'
        }}
      >
        <div style={{ flex: 1.2 }}>
          <h2 style={{ fontSize: '32px', color: isDarkMode ? '#c084fc' : '#7e22ce', marginBottom: '15px', fontWeight: '900' }}>Sequential Bilingual Mocks</h2>
          <p style={{ fontSize: '17px', color: isDarkMode ? '#e9d5ff' : '#6b21a8', lineHeight: '1.6', marginBottom: '25px' }}>
            Preparing for a dual-language requirement like <b>CRPF, CISF, or SSC?</b> Launch our full exam simulation. Complete a 10-minute English test, sit through a mandatory 2-minute countdown rest period, and immediately transition into your Hindi test—exactly as it happens in the exam hall.
          </p>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => { 
              setMenuTab("exams"); 
              setTimeout(() => {
                const targetSection = document.getElementById("sequential-track");
                if (targetSection) targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 150); 
            }} 
            style={{ padding: '14px 28px', backgroundColor: '#a855f7', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 8px 20px rgba(168, 85, 247, 0.3)' }}
          >
            Try a Mock Test 🚀
          </motion.button>
        </div>
        
        <div style={{ flex: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
          <motion.img 
            initial={{ opacity: 0, x: 30 }}
            animate={workflowLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            src="/bilingual-mock.webp" 
            alt="Bilingual Exam Workflow" 
            onLoad={() => setWorkflowLoaded(true)}
            loading="lazy"
            width="398"
            height="224"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              height: 'auto',
              borderRadius: '16px',
              filter: isDarkMode ? 'brightness(1.1) drop-shadow(0 10px 30px rgba(0,0,0,0.5))' : 'drop-shadow(0 10px 30px rgba(168, 85, 247, 0.2))'
            }} 
          />
        </div>
      </motion.div>

    </motion.div>
  );
}

// ==========================================
// 🚀 THE MAIN PLATFORM WEBSITE
// ==========================================
const MenuDashboard = ({ 
  openExamInfo,
  courseLanguage, setCourseLanguage,
  isPremium, currentUser, setCurrentUser, setAppState, 
  menuPage, setMenuPage, menuTab, setMenuTab, 
  statView, setStatView, soundEnabled, setSoundEnabled, 
  testMode, setTestMode, customText, setCustomText, 
  testDuration, setTestDuration, openDrillInfo, startTypingTest, 
  levels, lessonTitles,
  isDarkMode, setIsDarkMode,
  lang, setLang,
  authScreen 
}) => {
  
  const text = translations[lang] || translations['en'];

  const handleLogout = async () => {
    try {
      // 1. Sign out of Supabase backend
      await supabase.auth.signOut();
      
      // 2. Clear your local storage tokens if you have any custom ones
      if (currentUser) {
        localStorage.removeItem('activeSessionToken_' + currentUser);
      }
      
      // 3. Clear the state (just in case the reload takes a split second)
      setCurrentUser(null);
      
      // 4. THE FIX: Force a hard refresh of the page to reset ALL React state instantly
      window.location.reload(); 
      
    } catch (error) {
      console.error("Error logging out:", error.message);
    }
  };

  const [profileData, setProfileData] = useState({ email: 'Loading...', name: 'Loading...' });
  const [userPlan, setUserPlan] = useState('none'); 
  const [showAuthForm, setShowAuthForm] = useState(false); 
  const [premiumUntil, setPremiumUntil] = useState(null); 
  const [premiumSince, setPremiumSince] = useState(null);
  const [timeLeft, setTimeLeft] = useState('Calculating...'); 
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1000);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1000);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [tooltipData, setTooltipData] = useState(null);
  const [focusedLesson, setFocusedLesson] = useState(0);
  const [activeGame, setActiveGame] = useState(null);
  const [showKeyboardTip, setShowKeyboardTip] = useState(false);
  const [tipLayoutName, setTipLayoutName] = useState("");
  const [selectedExamId, setSelectedExamId] = useState(INDIAN_EXAMS[0].id);
  const [arcadeLevel, setArcadeLevel] = useState(1);
  const [arcadeLanguage, setArcadeLanguage] = useState(() => {
    return localStorage.getItem('arcadeLang') || 'english';
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [workflowLoaded, setWorkflowLoaded] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [examSearch, setExamSearch] = useState("");
  const [examFilter, setExamFilter] = useState("All");
  const [statsMode, setStatsMode] = useState("learning");
  // NEW SETTINGS STATE
  const [zenMode, setZenMode] = useState(() => localStorage.getItem('zenMode') === 'true');
  const [soundProfile, setSoundProfile] = useState(() => localStorage.getItem('soundProfile') || 'modern');
  const [cursorStyle, setCursorStyle] = useState(() => localStorage.getItem('cursorStyle') || 'line');
  const [isAwaitingPayment, setIsAwaitingPayment] = useState(false);


  // Save settings when they change
  useEffect(() => { localStorage.setItem('zenMode', zenMode); }, [zenMode]);
  useEffect(() => { localStorage.setItem('soundProfile', soundProfile); }, [soundProfile]);
  useEffect(() => { localStorage.setItem('cursorStyle', cursorStyle); }, [cursorStyle]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [menuTab]);
  
  useEffect(() => { 
    setActiveGame(null); 
    setShowAuthForm(false);
    setIsSidebarOpen(false);
  }, [menuTab]);
  
  useEffect(() => { setFocusedLesson(0); }, [menuPage]);

  const reportPaymentIssue = async (userEmail) => {
    console.warn(`🚨 PAYMENT ALERT: User ${userEmail} returned from checkout, but database was not upgraded.`);
    
    try {
      await emailjs.send(
        "service_ktc21vk",
        "template_fkz191i",
        {
          user_email: userEmail || "Unknown User",
          message: "A user returned to the site after clicking 'Select Plan', but Supabase did not mark them as Premium. Please check the Razorpay dashboard to see if their payment was Captured. If it was, manually upgrade their account in the Supabase 'users' table."
        },
        "KEPtGGP_Ra5MoidNm"
      );
      console.log("✅ Silent error alert sent to admin email successfully.");
    } catch (err) {
      console.error("Failed to send alert email:", err);
    }
  };

  // ==========================================
  // 💎 PREMIUM UPGRADE HANDLER (PAYMENT PAGES)
  // ==========================================
  const handleUpgrade = (planType) => {
    if (!currentUser) {
      alert("Please login or register first to upgrade your account!");
      setShowAuthForm(true);
      return;
    }

    const paymentLinks = {
      'weekly': 'https://rzp.io/rzp/YTammB5K',
      'monthly': 'https://rzp.io/rzp/ajxV4dHJ',
      '2-month': 'https://rzp.io/rzp/AtfGg7QB',
      '3-month': 'https://rzp.io/rzp/tby4HVaV',
      '6-month': 'https://rzp.io/rzp/Qej7SIn',
      'annually': 'https://rzp.io/rzp/SS5B9gO' 
    };

    const targetUrl = paymentLinks[planType];

    if (targetUrl) {
      const urlWithEmail = `${targetUrl}?email=${encodeURIComponent(currentUser)}`;
      
      // Open Razorpay in a new tab
      window.open(urlWithEmail, '_blank');
      
      // 🚀 Tell React to wait for the user to return!
      setIsAwaitingPayment(true); 
    } else {
      alert("Payment link configuration is missing for this plan.");
    }
  };

  // ==========================================
  // 🖨️ PDF REPORT CARD GENERATOR (Dynamically Loaded)
  // ==========================================
  const downloadReportCard = async () => {
    const reportElement = document.getElementById('official-report-card');
    reportElement.style.display = 'block';
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const canvas = await html2canvas(reportElement, { scale: 3, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${currentUser ? currentUser.split('@')[0] : 'Student'}_Typing_Report.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      reportElement.style.display = 'none';
    }
  };

  // ==========================================
  // 🚀 MAGIC ZIP CODE AUTO-FILL ENGINE (Silent & Smart)
  // ==========================================
  const handleZipCodeChange = async (e) => {
    const zip = e.target.value;
    setProfileDetails(prev => ({ ...prev, zipcode: zip }));

    if (zip.length >= 5 && profileDetails.country) {
      try {
        // Use the official India Post API for better District/City accuracy
        if (profileDetails.country === 'IN' && zip.length === 6) {
          const response = await fetch(`https://api.postalpincode.in/pincode/${zip}`);
          const data = await response.json();
          if (data[0].Status === 'Success') {
            const place = data[0].PostOffice[0];
            setProfileDetails(prev => ({
              ...prev,
              // Only auto-fill if the user hasn't typed anything there yet!
              city: prev.city ? prev.city : place.District,
              state: prev.state ? prev.state : place.State
            }));
          }
        } else {
          // Fallback for US/UK/Canada/Australia
          const response = await fetch(`https://api.zippopotam.us/${profileDetails.country}/${zip}`);
          if (response.ok) {
            const data = await response.json();
            const place = data.places[0];
            setProfileDetails(prev => ({
              ...prev,
              city: prev.city ? prev.city : place['place name'],
              state: prev.state ? prev.state : place['state']
            }));
          }
        }
      } catch (err) {
        console.log("Waiting for complete zip code...");
      }
    }
  };

  // ==========================================
  // 🏆 LEADERBOARD STATE & FETCH LOGIC
  // ==========================================
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [lbCategory, setLbCategory] = useState('exams'); // 'exams' or 'learning'
  const [isLoadingLb, setIsLoadingLb] = useState(false);

  // ==========================================
  // 👤 NEW PROFILE & DRAWER STATE
  // ==========================================
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [profileDetails, setProfileDetails] = useState({ phone: '', dob: '', address: '', city: '', state: '', country: 'IN', zipcode: '' });
  const [editName, setEditName] = useState("");

  // Load existing profile details when user logs in
  useEffect(() => {
    if (currentUser) {
      const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
      const savedDetails = db[currentUser]?.profileDetails || { phone: '', dob: '', address: '', city: '', state: '' };
      setProfileDetails(savedDetails);
    }
  }, [currentUser]);

  // Update editName when profileData loads
  useEffect(() => {
    if (profileData.name !== 'Loading...') setEditName(profileData.name);
  }, [profileData.name]);

  useEffect(() => {
    if (menuTab === 'leaderboard') {
      const fetchLeaderboard = async () => {
        setIsLoadingLb(true);
        try {
          // 🚀 ADDED 'email' TO THE SELECT QUERY
          const { data, error } = await supabase.from('users').select('email, username, progress_data');
          
          if (data && !error) {
            const processed = data.map(user => {
              const pData = user.progress_data || {};
              const examStats = pData.examStats || {};
              const stats = pData.stats || {};

              let maxLearningWpm = 0;
              Object.values(stats).forEach(s => {
                if (s.netWpm > maxLearningWpm) maxLearningWpm = s.netWpm;
              });

              let avgExamWpm = 0;
              if (examStats.attempts > 0) avgExamWpm = Math.round(examStats.sumNet / examStats.attempts);

              return {
                email: user.email, // 🚀 INCLUDE EMAIL
                username: user.username || 'Anonymous',
                maxLearningWpm,
                examAttempts: examStats.attempts || 0,
                avgExamWpm,
                avgExamAcc: examStats.attempts > 0 ? Math.round(examStats.sumAcc / examStats.attempts) : 0
              };
            });
            setLeaderboardData(processed);
          }
        } catch (err) {
          console.error("Failed to fetch leaderboard:", err);
        }
        setIsLoadingLb(false);
      };

      fetchLeaderboard();
    }
  }, [menuTab]);

  // Sort the data based on the selected category
  const rankedUsers = [...leaderboardData]
    .sort((a, b) => lbCategory === 'exams' ? b.avgExamWpm - a.avgExamWpm : b.maxLearningWpm - a.maxLearningWpm)
    .filter(u => lbCategory === 'exams' ? u.examAttempts > 0 : u.maxLearningWpm > 0)
    .slice(0, 50);

  const startIndex = (menuPage - 1) * 6;
  const currentLessonParts = levels.slice(startIndex, startIndex + 6);

  useEffect(() => {
    const fetchProfile = async () => {
      if (currentUser) { 
        try {
          // Get basic auth data
          const { data: { user } } = await supabase.auth.getUser();
          let realName = currentUser.split('@')[0];
          
          if (user) {
            realName = user.user_metadata?.full_name || user.user_metadata?.name || realName;
          }
          
          setProfileData({
            email: currentUser,
            name: realName
          });

          // 🚀 STEP 1: Try to fetch the user from the Table Editor
          let { data: userData, error: fetchError } = await supabase.from('users')
            .select('is_premium, plan_type, premium_until, premium_since, username, mobile, dob, address, city, state')
            .eq('email', currentUser)
            .single();

          // 🚀 STEP 2: THE SELF-HEALING SCRIPT
          // PGRST116 is the database error code for "No rows found". 
          // If they are missing, React will create the row instantly!
          if (fetchError && fetchError.code === 'PGRST116') {
              console.log("Missing row detected! Auto-healing database...");
              await supabase.from('users').insert([{ email: currentUser, username: realName }]);
              
              // Fetch the newly created row so the app doesn't break
              const retry = await supabase.from('users')
                .select('is_premium, plan_type, premium_until, premium_since, username, mobile, dob, address, city, state')
                .eq('email', currentUser)
                .single();
              userData = retry.data;
          } else if (!fetchError) {
              // If they already exist, just update their latest name to keep it fresh
              await supabase.from('users').update({ username: realName }).eq('email', currentUser);
          }

          // 🚀 STEP 3: Load Data into the UI safely
          if (userData) {
            if (userData.is_premium && (!userData.plan_type || userData.plan_type === 'none')) {
                setUserPlan('weekly'); 
            } else {
                setUserPlan(userData.plan_type || 'none');
            }
            setPremiumUntil(userData.premium_until);
            setPremiumSince(userData.premium_since); 

            setProfileDetails({
              phone: userData.mobile || '',
              dob: userData.dob || '',
              address: userData.address || '',
              city: userData.city || '',
              state: userData.state || '',
              country: userData.country || 'IN',
              zipcode: userData.zipcode || ''
            });
            if (userData.username) setEditName(userData.username);
          }
        } catch (err) {
          console.error("Error fetching profile:", err);
        }
      }
    };
    fetchProfile();
  }, [currentUser, menuTab]);

  useEffect(() => {
    if (!premiumUntil) return;
    const updateTimer = () => {
      const distance = new Date(premiumUntil).getTime() - new Date().getTime();
      if (distance <= 0) return setTimeLeft("Subscription Expired");
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${days}d ${hours}h ${minutes}m`);
    };
    updateTimer(); 
    const interval = setInterval(updateTimer, 60000); 
    return () => clearInterval(interval);
  }, [premiumUntil]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (menuTab !== "learning") return;

      // 🚀 Dynamically detect columns (3 for desktop, 1 for mobile)
      const cols = isMobile ? 1 : 3;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        // Move right normally, or flip to the next page if at the end of the 6 items
        if (focusedLesson + 1 < currentLessonParts.length) {
          setFocusedLesson(prev => prev + 1);
        } else {
          setMenuPage(prev => {
            const maxPage = Math.ceil(levels.length / 6);
            if (prev < maxPage) {
              setFocusedLesson(0); // Start at the first item of the next page
              return prev + 1;
            }
            return prev;
          });
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        // Move left normally, or flip to the previous page if at the beginning
        if (focusedLesson > 0) {
          setFocusedLesson(prev => prev - 1);
        } else {
          setMenuPage(prev => {
            if (prev > 1) {
              setFocusedLesson(5); // Go to the last item of the previous page
              return prev - 1;
            }
            return prev;
          });
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault(); 
        // Jump down perfectly by the current number of columns
        setFocusedLesson(prev => Math.min(currentLessonParts.length - 1, prev + cols)); 
      } else if (e.key === 'ArrowUp') {
        e.preventDefault(); 
        // Jump up perfectly by the current number of columns
        setFocusedLesson(prev => Math.max(0, prev - cols)); 
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const absoluteIndex = startIndex + focusedLesson;
        // 🚀 Locks from Lesson 3 (Page 3) onwards
        const isLocked = !isPremium && menuPage >= 3;

        if (isLocked) {
          alert(`🌟 Lessons from 3.1 onwards are a Premium Feature! Please ${!currentUser ? 'login and ' : ''}upgrade to unlock.`);
          setMenuTab(!currentUser ? "login" : "upgrade");
          return;
        }
        
        openDrillInfo(absoluteIndex); 
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [menuTab, focusedLesson, menuPage, currentLessonParts.length, startIndex, openDrillInfo, isMobile, isPremium, currentUser, levels.length]);

  // ==========================================
  // 🔄 AUTO-DETECT PAYMENT COMPLETION
  // ==========================================
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // If the user returns to our tab AND they were out making a payment
      if (document.visibilityState === 'visible' && isAwaitingPayment) {
        setIsAwaitingPayment(false); // Reset the flag immediately

        // Give the backend webhook 2.5 seconds to finish its math
        setTimeout(async () => {
          try {
            // Check Supabase for the latest data
            const { data, error } = await supabase
              .from('users')
              .select('is_premium, plan_type, premium_until')
              .eq('email', currentUser)
              .single();

            if (data && data.is_premium) {
              // ✅ PAYMENT SUCCESS: Update the UI instantly
              setIsPremium(true);
              setUserPlan(data.plan_type);
              setPremiumUntil(data.premium_until);
              alert("🎉 Payment Successful! Your Premium features are now unlocked.");
            } else {
              // ❌ PAYMENT INCOMPLETE OR FAILED
              alert("⚠️ Payment not completed. If money was deducted, it will sync within 24 hours.");
              
              // Trigger silent error log/email alert
              reportPaymentIssue(currentUser); 
            }
          } catch (err) {
            console.error("Error verifying payment status:", err);
          }
        }, 2500); 
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAwaitingPayment, currentUser]);

  // 🚀 REDIRECT TO HOME AFTER LOGIN
  useEffect(() => {
    if (currentUser && menuTab === "login") {
      setMenuTab("home");
    }
  }, [currentUser, menuTab, setMenuTab]);

  const tStyle = {
    bg: isDarkMode ? '#020617' : '#f1f5f9', 
    box: isDarkMode ? '#0f172a' : '#ffffff',
    sidebar: isDarkMode ? '#0f172a' : '#ffffff',
    colorText: isDarkMode ? '#f8fafc' : '#0f172a',
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#475569',
    border: isDarkMode ? '#1e293b' : '#e2e8f0',
    itemBg: isDarkMode ? '#1e293b' : '#f8fafc',
    accentBlue: isDarkMode ? '#38bdf8' : '#0369a1',
    accentGreen: isDarkMode ? '#4ade80' : '#10b981',
    inputBg: isDarkMode ? '#0f172a' : '#f8fafc'
  };
  
  const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
  const userKey = currentUser || 'Guest';

  // 🚀 THE FIX: Dynamically read the EXACT keyboard layout progress to show the green "Done" checkmarks
  const layoutSuffix = courseLanguage === 'english' ? '' : `_${courseLanguage}`;
  const compKey = `completedLevels${layoutSuffix}`;
  const statsKey = `stats${layoutSuffix}`;

  const userCompletedLevels = db[userKey]?.[compKey] || [];
  const userStats = db[userKey]?.[statsKey] || {};
  const statEntries = Object.entries(userStats).filter(([levelIndex]) => !String(levels[Number(levelIndex)]?.title || "").endsWith('.1')).sort((a, b) => Number(a[0]) - Number(b[0]));
  const totalDrills = statEntries.length;
  const bestWpm = totalDrills > 0 ? Math.max(...statEntries.map(e => e[1].netWpm)) : 0;
  const avgWpm = totalDrills > 0 ? Math.round(statEntries.reduce((acc, e) => acc + e[1].netWpm, 0) / totalDrills) : 0;
  const avgAcc = totalDrills > 0 ? Math.round(statEntries.reduce((acc, e) => acc + e[1].accuracy, 0) / totalDrills) : 0;
  const maxChartWpm = Math.max(50, bestWpm + 10); 
  const chartW = 800; const chartH = 240; const padX = 40; const padY = 30;
  const calcX = (index) => totalDrills > 1 ? padX + (index * ((chartW - padX * 2) / (totalDrills - 1))) : chartW / 2;
  const calcY = (val, max) => chartH - padY - ((val / max) * (chartH - padY * 2));
  const netPoints = statEntries.map((e, i) => `${calcX(i)},${calcY(e[1].netWpm, maxChartWpm)}`).join(' ');
  const netPolygon = totalDrills > 0 ? `${calcX(0)},${chartH - padY} ${netPoints} ${calcX(totalDrills - 1)},${chartH - padY}` : "";

  const userAchievements = currentUser ? (db[currentUser]?.achievements || []) : [];
  const allAchievements = [
    { id: 'First Steps', icon: '🌱', desc: 'Complete your very first typing drill.' },
    { id: 'Sharpshooter', icon: '🎯', desc: 'Complete a drill with 98% accuracy or higher.' },
    { id: 'Perfectionist', icon: '✨', desc: 'Complete a drill with a flawless 100% accuracy.' },
    { id: 'Speed Demon', icon: '⚡', desc: 'Reach a net speed of 30 WPM or higher.' },
    { id: 'Keyboard Ninja', icon: '🥷', desc: 'Reach an elite net speed of 50 WPM or higher.' },
    { id: 'Grandmaster', icon: '👑', desc: 'Reach a god-tier net speed of 70 WPM or higher.' },
    { id: 'Endurance Runner', icon: '🏃‍♂️', desc: 'Complete any long-form Paragraph (.6) drill.' },
    { id: 'Dedicated Learner', icon: '🎓', desc: 'Successfully complete 10 different typing drills.' }
  ];

  // 🚀 RECHARTS DATA FORMATTER
  const learningChartData = statEntries.map(e => ({
    lesson: `Part ${levels[Number(e[0])]?.title || e[0]}`,
    wpm: e[1].netWpm,
    accuracy: e[1].accuracy
  }));

  const handleTooltip = (e, stats, levelIndex, viewType) => {
    const rect = e.target.getBoundingClientRect();
    setTooltipData({ x: rect.left + rect.width / 2, y: rect.top, title: levels[Number(levelIndex)]?.title, stats: stats, view: viewType });
  };

  // 🚀 NAVIGATION CONFIGURATION
  const allNavItems = [
    { id: 'home', label: 'Home', color: isDarkMode ? '#38bdf8' : '#0369a1' },
    { id: 'learning', label: 'Learning', color: isDarkMode ? '#0ea5e9' : '#0369a1' },
    { id: 'exams', label: 'Indian Exams', color: isDarkMode ? '#eab308' : '#b45309' },
    { id: 'demo-test', label: 'Demo Test', color: isDarkMode ? '#8b5cf6' : '#6d28d9' },
    { id: 'tips', label: 'Tips & FAQs', color: isDarkMode ? '#f43f5e' : '#be123c', requiresLogin: true },
    { id: 'statistics', label: 'Statistics', color: isDarkMode ? '#10b981' : '#059669', requiresLogin: true },
    { id: 'leaderboard', label: 'Leaderboard', color: isDarkMode ? '#fbbf24' : '#b45309', requiresLogin: true },
    { id: 'shop', label: 'Gear Shop', color: isDarkMode ? '#f97316' : '#c2410c', requiresLogin: true },
    { id: 'games', label: 'Arcade', color: isDarkMode ? '#ef4444' : '#b91c1c', requiresLogin: true },
    { id: 'settings', label: 'Settings', color: isDarkMode ? '#64748b' : '#475569' }
  ];

  // 🚀 THE FIX: Filter tabs dynamically based on login status
  const navItems = currentUser ? allNavItems : allNavItems.filter(item => !item.requiresLogin);

  // 🚀 REUSABLE ANIMATION VARIANTS
  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 } 
    }
  };

  // 👇 CHANGED: Now drops in from the top (y: -40) and scales down from slightly larger (scale: 1.05)
  const slideInItem = {
    hidden: { opacity: 0, y: -40, scale: 1.05 }, 
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: "easeOut" } } 
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', overflowX: 'hidden', boxSizing: 'border-box', fontFamily: 'sans-serif', backgroundColor: tStyle.bg, transition: 'background-color 0.3s' }}>
      
      {/* 🚀 DYNAMIC SEO INJECTION */}
      <Helmet>
        <title>
          {menuTab === 'home' ? 'My Typing Coach | Official Government Exam Simulator' : 
           menuTab === 'exams' ? 'Indian Exams | My Typing Coach' : 
           menuTab === 'games' ? 'Typing Arcade | My Typing Coach' : 
           `My Typing Coach - ${menuTab.toUpperCase()}`}
        </title>
        {menuTab === 'exams' && (
          <meta name="description" content="Practice exact simulation layouts for SSC, UPSSSC, High Courts, and Railway typing skill tests." />
        )}
      </Helmet>
      
      {/* TOOLTIPS & MODALS */}
      {tooltipData && (
        <div style={{ position: 'fixed', left: tooltipData.x, top: tooltipData.y - 20, transform: 'translate(-50%, -100%)', backgroundColor: tStyle.box, border: `1px solid ${tStyle.border}`, borderRadius: '8px', padding: '12px 16px', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', zIndex: 9999, pointerEvents: 'none', minWidth: '150px' }}>
          <div style={{ fontSize: '11px', color: tStyle.accentBlue, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px', textAlign: 'center', borderBottom: `1px solid ${tStyle.border}`, paddingBottom: '4px' }}>Part {tooltipData.title}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span style={{ fontSize: '13px', color: tStyle.subtext }}>Speed:</span><span style={{ fontSize: '14px', fontWeight: 'bold', color: isDarkMode ? '#86efac' : '#15803d' }}>{tooltipData.stats.netWpm} WPM</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '13px', color: tStyle.subtext }}>Accuracy:</span><span style={{ fontSize: '14px', fontWeight: 'bold', color: isDarkMode ? '#fde047' : '#a16207' }}>{tooltipData.stats.accuracy}%</span></div>
        </div>
      )}
      <FeedbackModal currentUser={currentUser} isDarkMode={isDarkMode} isOpen={showFeedback} setIsOpen={setShowFeedback} />

      {/* ========================================== */}
      {/* 🚀 1. THE TOP NAVIGATION BAR               */}
      {/* ========================================== */}
      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 500, backgroundColor: tStyle.sidebar, borderBottom: `1px solid ${tStyle.border}`, padding: '15px 20px', width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        
        {/* 🚀 ADDED: flex: 1, minWidth: 0 to force the links to compress/scroll if needed! */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1, minWidth: 0, paddingRight: '15px' }}>
          
          {/* IMAGE LOGO */}
          <img 
            onClick={() => setMenuTab("home")} 
            src="/MTClogo.webp"
            alt="My Typing Coach"
            width="500"
            height="500"
            style={{ 
              width: '60px',
              height: 'auto', 
              cursor: 'pointer', 
              flexShrink: 0,
              objectFit: 'contain'
            }} 
          />

          {/* DESKTOP LINKS */}
          {!isMobile && (
            <div className="nav-scroller" style={{ 
              display: 'flex', 
              gap: '8px', 
              overflowX: 'auto', 
              whiteSpace: 'nowrap', 
              paddingBottom: '8px', /* Extra padding for the scrollbar */
              flex: 1,
              scrollbarWidth: 'thin', /* For Firefox */
              scrollbarColor: isDarkMode ? '#475569 transparent' : '#cbd5e1 transparent'
            }}>
              {/* 🚀 NEW: Custom Styled Scrollbar */}
              <style>{`
                .nav-scroller::-webkit-scrollbar { 
                  height: 6px; 
                }
                .nav-scroller::-webkit-scrollbar-track { 
                  background: transparent; 
                }
                .nav-scroller::-webkit-scrollbar-thumb { 
                  background-color: ${isDarkMode ? '#475569' : '#cbd5e1'}; 
                  border-radius: 10px; 
                }
                .nav-scroller::-webkit-scrollbar-thumb:hover { 
                  background-color: ${isDarkMode ? '#64748b' : '#94a3b8'}; 
                }
              `}</style>
              
              {navItems.map(item => (
                <button 
                  key={item.id} 
                  onClick={() => setMenuTab(item.id)} 
                  style={{ 
                    flexShrink: 0, 
                    padding: '8px 14px', 
                    backgroundColor: menuTab === item.id ? `${item.color}15` : 'transparent', 
                    color: menuTab === item.id ? item.color : tStyle.subtext, 
                    border: menuTab === item.id ? `1px solid ${item.color}50` : '1px solid transparent', 
                    borderRadius: '25px', 
                    cursor: 'pointer', 
                    fontWeight: 'bold', 
                    fontSize: '14px', 
                    transition: 'all 0.2s' 
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT CONTROLS - Profile Avatar & Mobile Menu */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexShrink: 0 }}>
           {!currentUser ? (
             <button onClick={() => setMenuTab("login")} style={{ padding: '8px 24px', backgroundColor: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '25px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: '0.2s', boxShadow: '0 4px 10px rgba(56, 189, 248, 0.3)' }}>
               Login
             </button>
           ) : (
             <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
               
               {/* 🚀 NEW PREMIUM / UPGRADE BADGE */}
               <button 
                 onClick={() => setMenuTab(isPremium ? "subscription" : "upgrade")} 
                 style={{ 
                   padding: '6px 16px', 
                   backgroundColor: isPremium ? '#000000' : 'transparent', 
                   color: isPremium ? '#FFD700' : '#f59e0b', 
                   border: isPremium ? '2px solid #FFD700' : '2px solid #f59e0b', 
                   borderRadius: '20px', 
                   fontWeight: 'bold', 
                   fontSize: '13px', 
                   cursor: 'pointer',
                   boxShadow: isPremium ? '0 4px 15px rgba(251, 191, 36, 0.3)' : 'none',
                   transition: 'all 0.2s'
                 }}
                 onMouseEnter={e => {
                   if (!isPremium) e.currentTarget.style.backgroundColor = isDarkMode ? '#451a03' : '#fef3c7';
                   else e.currentTarget.style.transform = 'scale(1.05)';
                 }}
                 onMouseLeave={e => {
                   if (!isPremium) e.currentTarget.style.backgroundColor = 'transparent';
                   else e.currentTarget.style.transform = 'scale(1)';
                 }}
               >
                 {isPremium ? 'Premium' : 'Upgrade'}
               </button>

               {/* PROFILE AVATAR (Crown Emoji Removed) */}
               <button 
                 onClick={() => setIsProfileDrawerOpen(true)} 
                 style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: '#0ea5e9', color: 'white', fontWeight: 'bold', fontSize: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: `2px solid ${tStyle.border}`, cursor: 'pointer', boxShadow: '0 4px 10px rgba(14, 165, 233, 0.3)', padding: 0 }}
                 title="Open Profile"
               >
                 {profileData.name !== 'Loading...' ? profileData.name.charAt(0).toUpperCase() : 'U'}
               </button>
               
             </div>
           )}
           
           {isMobile && (
             <button onClick={() => setIsSidebarOpen(true)} style={{ background: 'none', border: 'none', color: tStyle.colorText, fontSize: '28px', cursor: 'pointer' }}>☰</button>
           )}
        </div>
      </header>

      {/* ========================================== */}
      {/* 🚀 THE NEW PROFILE SLIDE-OUT DRAWER        */}
      {/* ========================================== */}
      {isProfileDrawerOpen && <div onClick={() => setIsProfileDrawerOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(2px)' }} />}
      <div style={{ position: 'fixed', top: 0, right: isProfileDrawerOpen ? '0' : '-350px', width: '320px', height: '100%', backgroundColor: tStyle.itemBg, zIndex: 1005, transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: isProfileDrawerOpen ? '-10px 0 30px rgba(0,0,0,0.5)' : 'none', display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${tStyle.border}` }}>
        
        {/* Close Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px' }}>
          <button onClick={() => setIsProfileDrawerOpen(false)} aria-label="Close Profile" style={{ background: 'none', border: 'none', fontSize: '28px', color: tStyle.subtext, cursor: 'pointer' }}>&times;</button>
        </div>

        {/* User Info Header */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 20px 20px 20px', borderBottom: `1px solid ${tStyle.border}` }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#0ea5e9', color: 'white', fontSize: '32px', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '15px', boxShadow: '0 8px 20px rgba(14, 165, 233, 0.3)' }}>
             {profileData.name !== 'Loading...' ? profileData.name.charAt(0).toUpperCase() : 'U'}
          </div>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', color: tStyle.colorText }}>Hello, {profileData.name !== 'Loading...' ? profileData.name.split(' ')[0] : 'User'}</h3>
          <p style={{ margin: 0, fontSize: '13px', color: tStyle.subtext }}>{currentUser}</p>
        </div>

        {/* Drawer Buttons */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px', borderBottom: `1px solid ${tStyle.border}` }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => { setMenuTab("edit-profile"); setIsProfileDrawerOpen(false); }} style={{ flex: 1, padding: '10px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
              ✏️ Edit Profile
            </button>
            <button onClick={() => { handleLogout(); setIsProfileDrawerOpen(false); }} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
              🚪 Logout
            </button>
          </div>
          <button onClick={() => { setMenuTab("subscription"); setIsProfileDrawerOpen(false); }} style={{ width: '100%', padding: '12px', backgroundColor: 'transparent', color: tStyle.colorText, border: `2px solid ${tStyle.border}`, borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
             💳 My Subscription
          </button>
        </div>

        {/* Support Footer */}
        <div style={{ padding: '20px', marginTop: 'auto', textAlign: 'center', fontSize: '13px', color: tStyle.subtext }}>
          <p style={{ margin: '0 0 5px 0' }}>Support Helpline: <b>+91 8873900803</b></p>
          <p style={{ margin: 0 }}>mytypingcoach17@gmail.com</p>
        </div>
      </div>

      {/* ========================================== */}
      {/* 🚀 2. MOBILE DRAWER                          */}
      {/* ========================================== */}
      {isMobile && isSidebarOpen && <div onClick={() => setIsSidebarOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 145, backdropFilter: 'blur(3px)' }} />}
      {isMobile && (
        <div style={{ position: 'fixed', top: 0, right: isSidebarOpen ? '0' : '-280px', width: '260px', height: '100%', backgroundColor: tStyle.sidebar, zIndex: 150, transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: isSidebarOpen ? '-5px 0 25px rgba(0,0,0,0.5)' : 'none', display: 'flex', flexDirection: 'column', padding: '20px' }}>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: `1px solid ${tStyle.border}`, paddingBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', color: tStyle.colorText, fontSize: '18px' }}>Menu Navigation</div>
              <button onClick={() => setIsSidebarOpen(false)} aria-label="Close Menu" style={{ background: 'none', border: 'none', color: tStyle.colorText, fontSize: '28px', cursor: 'pointer' }}>&times;</button>
           </div>
           <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto' }}>
              {navItems.map(item => (
                 <button key={item.id} onClick={() => { setMenuTab(item.id); setIsSidebarOpen(false); }} style={{ padding: '14px 16px', textAlign: 'left', backgroundColor: menuTab === item.id ? `${item.color}20` : 'transparent', color: menuTab === item.id ? item.color : tStyle.subtext, border: menuTab === item.id ? `2px solid ${item.color}50` : '2px solid transparent', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                   <span style={{ fontSize: '18px' }}>{item.icon}</span> {item.label}
                 </button>
              ))}
           </div>
        </div>
      )}

      {/* ========================================== */}
      {/* 🚀 3. THE WIDE OPEN CANVAS                 */}
      {/* ========================================== */}
      <main style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: isMobile ? '100px 10px 20px 10px' : '110px 20px 40px 20px', overflowY: 'auto', width: '100%', boxSizing: 'border-box' }}>
         
         {/* 🚀 INJECT PROMO BANNER HERE */}
         <PromoBanner isDarkMode={isDarkMode} setMenuTab={setMenuTab} />

         <div style={{ width: '100%', maxWidth: '1100px', backgroundColor: tStyle.box, borderRadius: '24px', padding: isMobile ? '20px' : '40px', boxShadow: isDarkMode ? '0 10px 40px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.05)', border: `1px solid ${tStyle.border}`, minHeight: '600px', display: 'flex', flexDirection: 'column' }}>

          {/* ========================================== */}
           {/* TAB: HOME (The "Learn + Exam" Strategy)      */}
           {/* ========================================== */}
           {menuTab === "home" && (
             <HomePageTab isDarkMode={isDarkMode} isMobile={isMobile} setMenuTab={setMenuTab} />
           )}
           
           {/* ========================================== */}
           {/* 1. TAB: LOGIN PAGE                         */}
           {/* ========================================== */}
           {menuTab === "login" && (
            <motion.div key="login" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              {authScreen}
            </motion.div>
           )}

           {/* ========================================== */}
           {/* 2. TAB: EDIT PROFILE                       */}
           {/* ========================================== */}
           {menuTab === "edit-profile" && (
            <motion.div key="edit-profile" variants={staggerContainer} initial="hidden" animate="show" style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
              <motion.h2 variants={slideInItem} style={{ color: tStyle.colorText, margin: '0 0 20px 0', fontSize: '28px', borderBottom: `2px solid ${tStyle.border}`, paddingBottom: '15px' }}>Edit Profile</motion.h2>
              
              <motion.form 
                variants={slideInItem} 
                onSubmit={async (e) => {
                  e.preventDefault();
                  
                  // 🚀 SEND DIRECTLY TO SUPABASE COLUMNS (Including new ones)
                  const { error } = await supabase.from('users').update({ 
                    username: editName, 
                    mobile: profileDetails.phone,
                    dob: profileDetails.dob,
                    address: profileDetails.address,
                    city: profileDetails.city,
                    state: profileDetails.state,
                    country: profileDetails.country, // 🚀 ADDED
                    zipcode: profileDetails.zipcode  // 🚀 ADDED
                  }).eq('email', currentUser);

                  if (error) {
                    alert("Error saving profile: " + error.message);
                    return;
                  }

                  // Update Local State UI
                  setProfileData(prev => ({ ...prev, name: editName }));
                  
                  // Save a backup to localStorage just in case
                  const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
                  if (!db[currentUser]) db[currentUser] = {};
                  db[currentUser].profileDetails = profileDetails;
                  localStorage.setItem('typeFasterDB', JSON.stringify(db));

                  alert("Profile updated successfully in the cloud!");
                  setMenuTab("home");
                }} 
                style={{ display: 'flex', flexDirection: 'column', gap: '20px', backgroundColor: tStyle.itemBg, padding: '30px', borderRadius: '16px', border: `1px solid ${tStyle.border}` }}
              >
                <div><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Username</label><input type="text" value={editName} onChange={e=>setEditName(e.target.value)} required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box' }} /></div>
                <div><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Email (Locked)</label><input type="email" value={currentUser} disabled style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: isDarkMode ? '#020617' : '#e2e8f0', color: tStyle.subtext, boxSizing: 'border-box', cursor: 'not-allowed' }} /></div>
                
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Phone Number</label><input type="tel" value={profileDetails.phone} onChange={e=>setProfileDetails({...profileDetails, phone: e.target.value})} placeholder="Phone number" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box' }} /></div>
                  <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Date of Birth</label><input type="date" value={profileDetails.dob} onChange={e=>setProfileDetails({...profileDetails, dob: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box', colorScheme: isDarkMode ? 'dark' : 'light' }} /></div>
                </div>

                <div><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Address</label><input type="text" value={profileDetails.address} onChange={e=>setProfileDetails({...profileDetails, address: e.target.value})} placeholder="Street address" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box' }} /></div>
                
                {/* 🚀 COUNTRY AND SILENT ZIP CODE */}
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Country</label>
                    <select 
                      value={profileDetails.country} 
                      onChange={e=>setProfileDetails({...profileDetails, country: e.target.value})} 
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box', cursor: 'pointer', outline: 'none' }}
                    >
                      <option value="IN">India</option>
                      <option value="US">United States</option>
                      <option value="GB">United Kingdom</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>Zip / Pin Code</label>
                    <input type="text" value={profileDetails.zipcode} onChange={handleZipCodeChange} placeholder="Pin Code" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                </div>

                {/* 🚀 STATE DROPDOWN AND CITY */}
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>State / Province</label>
                    {profileDetails.country === 'IN' ? (
                      <select 
                        value={profileDetails.state} 
                        onChange={e=>setProfileDetails({...profileDetails, state: e.target.value})} 
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box', cursor: 'pointer', outline: 'none' }}
                      >
                        <option value="" disabled>Select State</option>
                        {["Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli", "Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"].map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" value={profileDetails.state} onChange={e=>setProfileDetails({...profileDetails, state: e.target.value})} placeholder="State" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box' }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px' }}>City</label>
                    <input type="text" value={profileDetails.city} onChange={e=>setProfileDetails({...profileDetails, city: e.target.value})} placeholder="City" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, boxSizing: 'border-box' }} />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                  <button type="button" onClick={() => setMenuTab("home")} style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: '#f97316', border: '2px solid #f97316', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>CANCEL</button>
                  <button type="submit" style={{ flex: 1, padding: '14px', backgroundColor: tStyle.colorText, color: tStyle.bg, border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>UPDATE</button>
                </div>
              </motion.form>
            </motion.div>
           )}

           {/* ========================================== */}
           {/* 3. TAB: MY SUBSCRIPTION                    */}
           {/* ========================================== */}
           {menuTab === "subscription" && (
            <motion.div key="subscription" variants={staggerContainer} initial="hidden" animate="show" style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${tStyle.border}`, paddingBottom: '15px', marginBottom: '30px' }}>
                <motion.h2 variants={slideInItem} style={{ color: tStyle.colorText, margin: 0, fontSize: '28px' }}>My Subscription</motion.h2>
                <span style={{ fontSize: '13px', color: tStyle.subtext }}>Login again to sync latest status.</span>
              </div>
              
              <motion.div variants={slideInItem} style={{ backgroundColor: tStyle.itemBg, borderRadius: '16px', border: `1px solid ${tStyle.border}`, padding: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                
                {/* Header Card */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px', borderBottom: `1px solid ${tStyle.border}`, marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: isPremium ? '#10b981' : tStyle.inputBg, border: `1px solid ${isPremium ? '#059669' : tStyle.border}`, display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '20px' }}>
                      {isPremium ? '👑' : '❕'}
                    </div>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', fontSize: '22px', color: tStyle.colorText, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isPremium ? `${userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} Plan` : 'Free Plan'}
                        <span style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: isPremium ? '#064e3b' : tStyle.inputBg, color: isPremium ? '#34d399' : tStyle.subtext, borderRadius: '12px', border: `1px solid ${isPremium ? '#059669' : tStyle.border}` }}>
                          {isPremium ? 'PREMIUM' : 'FREE'}
                        </span>
                      </h3>
                      <p style={{ margin: 0, fontSize: '14px', color: tStyle.subtext }}>
                        {isPremium ? "You have full access to all mock exams and analytics." : "You are currently on Free plan. Upgrade to unlock full access."}
                      </p>
                    </div>
                  </div>
                  {!isPremium && <button onClick={() => setMenuTab("upgrade")} style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)' }}>💳 Upgrade</button>}
                </div>

                {/* Details List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: tStyle.colorText, fontSize: '15px' }}>
                    <span>Enrolled</span> 
                    {/* 🚀 NOW IT PULLS THE REAL DATE FROM THE DATABASE! */}
                    <span>{isPremium ? (premiumSince ? new Date(premiumSince).toLocaleDateString() : new Date().toLocaleDateString()) : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: tStyle.colorText, fontSize: '15px' }}>
                    <span>Valid until</span> 
                    <span>{isPremium ? new Date(premiumUntil).toLocaleDateString() : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: tStyle.colorText, fontSize: '15px' }}>
                    <span>Days remaining</span> 
                    <span style={{ fontWeight: 'bold', color: isPremium ? '#10b981' : tStyle.colorText }}>
                      {isPremium ? Math.max(0, Math.ceil((new Date(premiumUntil).getTime() - Date.now()) / (1000*60*60*24))) : '—'}
                    </span>
                  </div>
                </div>

                {/* Features Box */}
                <div style={{ backgroundColor: tStyle.inputBg, borderRadius: '12px', padding: '20px', border: `1px solid ${tStyle.border}` }}>
                  <h4 style={{ margin: '0 0 15px 0', color: tStyle.colorText, fontSize: '16px' }}>{isPremium ? 'Premium plan includes' : 'Free plan includes'}</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', color: tStyle.subtext, fontSize: '14px' }}>
                    <li style={{ display: 'flex', gap: '10px' }}>✅ <span>Exam-based typing rules & evaluation</span></li>
                    <li style={{ display: 'flex', gap: '10px' }}>✅ <span>{isPremium ? 'Unlimited mock tests and history tracking' : 'Full access to Basic Lessons (1-6)'}</span></li>
                    <li style={{ display: 'flex', gap: '10px' }}>{isPremium ? '✅' : '🔒'} <span>{isPremium ? 'Advanced Analytics & Bilingual Sequential Mocks' : 'Upgrade to unlock Advanced Lessons (7-12) & Mocks'}</span></li>
                  </ul>
                </div>

                {isPremium && (
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button onClick={() => setMenuTab("upgrade")} style={{ padding: '12px 24px', backgroundColor: 'transparent', color: '#0ea5e9', border: '2px solid #0ea5e9', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Extend Subscription</button>
                  </div>
                )}
              </motion.div>
            </motion.div>
           )}

           {/* ========================================== */}
           {/* 4. TAB: UPGRADE PLANS                      */}
           {/* ========================================== */}
           {menuTab === "upgrade" && (
            <motion.div key="upgrade" variants={staggerContainer} initial="hidden" animate="show" style={{ maxWidth: '900px', margin: '0 auto', width: '100%', paddingBottom: '40px' }}>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <motion.h2 variants={slideInItem} style={{ color: tStyle.colorText, margin: '0 0 10px 0', fontSize: '32px' }}>Choose Your Success Plan</motion.h2>
                <motion.p variants={slideInItem} style={{ color: tStyle.subtext, fontSize: '16px' }}>Unlock the official mock exam engine, advanced analytics, and all premium lessons.</motion.p>
              </div>

              <motion.div variants={slideInItem} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                
                {/* Weekly */}
                <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Weekly Pass</h3>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: tStyle.colorText, marginBottom: '15px' }}>₹15<span style={{ fontSize: '14px', color: tStyle.subtext, fontWeight: 'normal' }}> /week</span></div>
                  <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', flexGrow: 1 }}>Perfect for an intense final week of practice right before your exam date.</p>
                  <button onClick={() => handleUpgrade('weekly')} style={{ width: '100%', padding: '14px', backgroundColor: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Select Plan</button>
                </div>

                {/* Monthly (Popular) */}
                <div style={{ backgroundColor: isDarkMode ? '#1e3a8a' : '#eff6ff', border: `2px solid #3b82f6`, borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', position: 'relative', transform: 'scale(1.05)', zIndex: 10, boxShadow: '0 10px 30px rgba(59, 130, 246, 0.2)' }}>
                  <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#f59e0b', color: '#fff', fontSize: '12px', fontWeight: 'bold', padding: '4px 16px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Most Popular</div>
                  <h3 style={{ margin: '0 0 10px 0', color: isDarkMode ? '#93c5fd' : '#1d4ed8', fontSize: '20px' }}>Monthly Mastery</h3>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: tStyle.colorText, marginBottom: '15px' }}>₹49<span style={{ fontSize: '14px', color: tStyle.subtext, fontWeight: 'normal' }}> /month</span></div>
                  <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', flexGrow: 1 }}>The ideal timeframe to build muscle memory from scratch and clear SSC cutoffs.</p>
                  <button onClick={() => handleUpgrade('monthly')} style={{ width: '100%', padding: '14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>Select Plan</button>
                </div>

                {/* 2-Month */}
                <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>2-Month Prep</h3>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: tStyle.colorText, marginBottom: '15px' }}>₹89<span style={{ fontSize: '14px', color: tStyle.subtext, fontWeight: 'normal' }}> /2 months</span></div>
                  <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', flexGrow: 1 }}>Designed for back-to-back state board and central government exam schedules.</p>
                  <button 
                    onClick={() => handleUpgrade('2-month')} 
                    style={{ 
                      width: '100%', padding: '14px', 
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', 
                      color: isDarkMode ? '#f8fafc' : '#1e293b', 
                      border: isDarkMode ? '1.5px solid #475569' : '1.5px solid #94a3b8', 
                      borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = '#2563eb'; e.target.style.color = '#2563eb'; }}
                    onMouseLeave={e => { e.target.style.borderColor = isDarkMode ? '#475569' : '#94a3b8'; e.target.style.color = isDarkMode ? '#f8fafc' : '#1e293b'; }}
                  >
                    Select Plan
                  </button>
                </div>

                {/* 3-Month */}
                <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>3-Month Bilingual</h3>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: tStyle.colorText, marginBottom: '15px' }}>₹129<span style={{ fontSize: '14px', color: tStyle.subtext, fontWeight: 'normal' }}> /3 months</span></div>
                  <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', flexGrow: 1 }}>Enough time to achieve elite 40+ WPM speeds in both English and Hindi layouts.</p>
                  <button 
                    onClick={() => handleUpgrade('3-month')} 
                    style={{ 
                      width: '100%', padding: '14px', 
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', 
                      color: isDarkMode ? '#f8fafc' : '#1e293b', 
                      border: isDarkMode ? '1.5px solid #475569' : '1.5px solid #94a3b8', 
                      borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = '#2563eb'; e.target.style.color = '#2563eb'; }}
                    onMouseLeave={e => { e.target.style.borderColor = isDarkMode ? '#475569' : '#94a3b8'; e.target.style.color = isDarkMode ? '#f8fafc' : '#1e293b'; }}
                  >
                    Select Plan
                  </button>
                </div>

                {/* 6-Month */}
                <div style={{ backgroundColor: tStyle.itemBg, border: `1px solid ${tStyle.border}`, borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Half-Year Dedicated</h3>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: tStyle.colorText, marginBottom: '15px' }}>₹229<span style={{ fontSize: '14px', color: tStyle.subtext, fontWeight: 'normal' }}> /6 months</span></div>
                  <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', flexGrow: 1 }}>Uninterrupted access. Keep your fingers sharp across the entire exam calendar year.</p>
                  <button 
                    onClick={() => handleUpgrade('6-month')} 
                    style={{ 
                      width: '100%', padding: '14px', 
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', 
                      color: isDarkMode ? '#f8fafc' : '#1e293b', 
                      border: isDarkMode ? '1.5px solid #475569' : '1.5px solid #94a3b8', 
                      borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = '#2563eb'; e.target.style.color = '#2563eb'; }}
                    onMouseLeave={e => { e.target.style.borderColor = isDarkMode ? '#475569' : '#94a3b8'; e.target.style.color = isDarkMode ? '#f8fafc' : '#1e293b'; }}
                  >
                    Select Plan
                  </button>
                </div>

                {/* Annual (Best Value) - 50% OFF PROMO */}
                <div style={{ backgroundColor: isDarkMode ? '#064e3b' : '#f0fdf4', border: `2px solid #10b981`, borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', position: 'relative', transform: 'scale(1.05)', zIndex: 10, boxShadow: '0 10px 30px rgba(16, 185, 129, 0.2)' }}>
                  <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#10b981', color: '#fff', fontSize: '12px', fontWeight: 'bold', padding: '4px 16px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Best Value</div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: '0 0 10px 0', color: '#10b981', fontSize: '20px' }}>Annual Pro</h3>
                    <span style={{ backgroundColor: '#ef4444', color: 'white', padding: '4px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>50% OFF SALE</span>
                  </div>

                  {/* Pricing with Strikethrough */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '15px' }}>
                    <span style={{ fontSize: '20px', color: tStyle.subtext, textDecoration: 'line-through', fontWeight: 'bold' }}>₹399</span>
                    <span style={{ fontSize: '32px', fontWeight: '900', color: tStyle.colorText }}>₹199</span>
                    <span style={{ fontSize: '14px', color: tStyle.subtext, fontWeight: 'normal' }}> /year</span>
                  </div>
                  
                  <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', flexGrow: 1 }}>The ultimate value. Pay once, forget about renewals, and secure your career. <strong style={{color: '#ef4444'}}>Diwali Special Festive Offer!</strong></p>
                  <button onClick={() => handleUpgrade('annually')} style={{ width: '100%', padding: '14px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)' }}>Select Plan</button>
                </div>

              </motion.div>
            </motion.div>
           )}

           {/* TAB: LEARNING */}
           {menuTab === "learning" && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
              
              {/* Header Section with Top-Right Language Switcher */}
              <div style={{ position: 'relative', width: '100%', marginBottom: '20px' }}>
                
                {/* 🚀 5-KEYBOARD DROPDOWN */}
                <div style={{ position: isMobile ? 'relative' : 'absolute', right: 0, top: 0, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', marginBottom: isMobile ? '15px' : '0', zIndex: 10 }}>
                  <select 
                  value={courseLanguage} 
                  onChange={(e) => { 
                    const newLang = e.target.value;
                    setCourseLanguage(newLang); 
                    localStorage.setItem('courseLang', newLang); 
                  
                    if (newLang !== 'english') {
                      const names = {
                        kruti_dev: "Kruti Dev 010",
                        inscript: "Mangal InScript",
                        remington_gail: "Remington GAIL",
                        remington_cbi: "Remington CBI"
                      };
                      setTipLayoutName(names[newLang]);
                      setShowKeyboardTip(true);
                    }
                  }} 
                  style={{ 
                    padding: '8px 16px', 
                    borderRadius: '8px', 
                    border: `2px solid ${tStyle.border}`, 
                    backgroundColor: tStyle.itemBg, 
                    color: tStyle.colorText, 
                    fontWeight: 'bold', 
                    cursor: 'pointer', 
                    outline: 'none', 
                    fontSize: '14px', 
                    boxShadow: '0 4px 10px rgba(0,0,0,0.05)' 
                  }}
                >
                    <option value="english">🇺🇸 English</option>
                    <option value="kruti_dev">🇮🇳 Hindi (Kruti Dev 010)</option>
                    <option value="inscript">🇮🇳 Hindi (Mangal InScript)</option>
                    <option value="remington_gail">🇮🇳 Hindi (Remington GAIL)</option>
                    <option value="remington_cbi">🇮🇳 Hindi (Remington CBI)</option>
                  </select>
                </div>

                {/* Centered Titles */}
                <div style={{ textAlign: 'center' }}>
                  <h2 style={{ color: tStyle.accentBlue, margin: '0 0 10px 0', fontSize: '26px' }}>{text.learningStage}</h2>
                  <h3 style={{ color: tStyle.subtext, margin: 0, fontSize: '15px' }}>{lessonTitles[menuPage - 1]}</h3>
                </div>
              </div>
              
              {/* 🚀 UNIVERSAL LESSONS GRID */}
              <motion.div key={`learning-grid-${courseLanguage}`} variants={staggerContainer} initial="hidden" animate="show" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px', marginBottom: '30px', flexGrow: 1 }}>
                {currentLessonParts.map((part, index) => {
                  const absoluteIndex = startIndex + index;
                  const isCompleted = userCompletedLevels.includes(absoluteIndex);
                  const isHighlighted = index === focusedLesson;
                  // 🚀 Locks from Lesson 3 (Page 3) onwards
                  const isLocked = !isPremium && menuPage >= 3; 
                  
                  return (
                    <motion.button 
                     variants={slideInItem}
                     key={absoluteIndex} 
                     onClick={() => {
                       if (isLocked) { 
                         alert(`🌟 Lessons from 3.1 onwards are a Premium Feature! Please ${!currentUser ? 'login and ' : ''}upgrade to unlock.`); 
                         setMenuTab(!currentUser ? "login" : "upgrade"); 
                         return; 
                       } 
                       // 🚀 Removed the annoying confirm popup!
                       openDrillInfo(absoluteIndex);
                     }} 
                     onMouseEnter={() => setFocusedLesson(index)}
                      style={{ padding: '20px', backgroundColor: isLocked ? (isDarkMode ? '#0f172a' : '#e2e8f0') : (isCompleted ? (isDarkMode ? '#064e3b' : '#f0fdf4') : tStyle.itemBg), border: isLocked ? `2px dashed ${tStyle.subtext}` : (isCompleted ? (isDarkMode ? '2px solid #059669' : '2px solid #22c55e') : `2px solid ${tStyle.border}`), borderRadius: '12px', cursor: isLocked ? 'not-allowed' : 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '8px', outline: isHighlighted ? `4px solid ${isDarkMode ? '#38bdf8' : '#0ea5e9'}` : 'none', transform: isHighlighted && !isLocked ? 'scale(1.02)' : 'scale(1)', transition: 'all 0.1s ease-in-out', zIndex: isHighlighted ? 10 : 1, opacity: isLocked ? 0.6 : 1 }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                         <span style={{ fontWeight: 'bold', fontSize: '16px', color: isLocked ? tStyle.subtext : (isCompleted ? tStyle.accentGreen : tStyle.accentBlue) }}> {isLocked && "🔒 "}Part {part.title}</span>
                         {isCompleted && !isLocked && <span style={{ fontSize: '11px', backgroundColor: '#22c55e', color: 'white', padding: '4px 8px', borderRadius: '6px', fontWeight: 'bold' }}>{text.partDone}</span>}
                      </div>
                      
                      {/* THIS IS THE DYNAMIC INSTRUCTION TEXT */}
                      <span style={{ fontSize: '13px', color: tStyle.subtext, lineHeight: '1.5' }}>{part.instruction}</span>
                    </motion.button>
                  );
                })}
              </motion.div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', borderTop: `1px solid ${tStyle.border}`, paddingTop: '20px' }}>
                <button onClick={() => setMenuPage(prev => Math.max(1, prev - 1))} disabled={menuPage === 1} style={{ padding: '12px 24px', backgroundColor: menuPage === 1 ? tStyle.itemBg : '#0ea5e9', color: menuPage === 1 ? tStyle.subtext : 'white', border: 'none', borderRadius: '8px', cursor: menuPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '15px' }}>{text.btnPrev}</button>
                
                {/* 🚀 DYNAMIC TOTAL PAGES */}
                <span style={{ fontWeight: 'bold', color: tStyle.colorText, fontSize: '16px' }}>
                  Page {menuPage} of {Math.ceil(levels.length / 6)}
                </span>
                
                {/* 🚀 DYNAMIC NEXT BUTTON */}
                <button 
                  onClick={() => setMenuPage(prev => Math.min(Math.ceil(levels.length / 6), prev + 1))} 
                  disabled={menuPage === Math.ceil(levels.length / 6)} 
                  style={{ padding: '12px 24px', backgroundColor: menuPage === Math.ceil(levels.length / 6) ? tStyle.itemBg : '#0ea5e9', color: menuPage === Math.ceil(levels.length / 6) ? tStyle.subtext : 'white', border: 'none', borderRadius: '8px', cursor: menuPage === Math.ceil(levels.length / 6) ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '15px' }}
                >
                  {text.btnNext}
                </button>
              </div>
            </div>
           )}

           {/* TAB: PRO TYPING TEST */}
           {menuTab === "demo-test" && (
            <motion.div 
              key="demo-test"
              initial={{ opacity: 0, y: -40, scale: 1.05 }} 
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
            >
              <Suspense fallback={<div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading test engine...</div>}>
                <AdvancedTypingTest isDarkMode={isDarkMode} isPremium={isPremium} setMenuTab={setMenuTab} startTypingTest={startTypingTest} />
              </Suspense>
            </motion.div>
           )}

           {/* TAB: INDIAN EXAMS */}
           {menuTab === "exams" && (
             <motion.div 
               key="exams"
               initial={{ opacity: 0, y: -40, scale: 1.05 }} 
               animate={{ opacity: 1, y: 0, scale: 1 }}
               transition={{ duration: 0.5, ease: "easeOut" }}
               style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
             >
               
              <div style={{ width: '100%', maxWidth: '1000px', margin: '0 auto', padding: '20px 0' }}>
                
                {/* Page Title Header */}
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  <h2 style={{ fontSize: '30px', fontWeight: '800', color: isDarkMode ? '#38bdf8' : '#0284c7', margin: '0 0 12px 0', letterSpacing: '-0.5px' }}>
                    <span style={{ color: isDarkMode ? '#f8fafc' : '#0f172a' }}>IN</span> Indian Government Exam Engine
                  </h2>
                  <p style={{ color: isDarkMode ? '#94a3b8' : '#475569', fontSize: '16px', margin: 0, maxWidth: '700px', mx: 'auto', lineHeight: '1.6', display: 'inline-block' }}>
                    Select your exact authorized typing medium. Clicking an option automatically configures the system with the targeted keyboard mapping layout, evaluation matrix, and backspace restrictions.
                  </p>
                </div>

                {/* 🚀 NEW: SEARCH AND FILTER CONTROLS */}
                <div style={{ marginBottom: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                  {/* Search Bar */}
                  <div style={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
                    <input 
                      type="text" 
                      placeholder="Search exams (e.g., LDC, High Court, SSC)..."
                      value={examSearch}
                      onChange={(e) => setExamSearch(e.target.value)}
                      style={{ 
                        width: '100%', padding: '14px 20px 14px 45px', borderRadius: '12px', fontSize: '15px',
                        border: `2px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`, 
                        backgroundColor: isDarkMode ? '#1e293b' : 'white', color: isDarkMode ? '#f8fafc' : '#0f172a',
                        outline: 'none', transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = '#38bdf8'}
                      onBlur={e => e.target.style.borderColor = isDarkMode ? '#334155' : '#cbd5e1'}
                    />
                    <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: isDarkMode ? '#64748b' : '#94a3b8' }}>🔍</span>
                  </div>

                  {/* Filter Chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
                    {["All", "SSC", "Railway", "Defense & Police", "Judiciary", "State Boards", "Central Govt"].map(category => (
                      <button
                        key={category}
                        onClick={() => setExamFilter(category)}
                        style={{
                          padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s',
                          backgroundColor: examFilter === category ? (isDarkMode ? '#38bdf8' : '#0284c7') : (isDarkMode ? '#1e293b' : '#f1f5f9'),
                          color: examFilter === category ? 'white' : (isDarkMode ? '#94a3b8' : '#475569'),
                          border: `1px solid ${examFilter === category ? 'transparent' : (isDarkMode ? '#334155' : '#cbd5e1')}`
                        }}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 🚀 DYNAMIC FILTER LOGIC */}
                {(() => {
                  // Filter the exams based on search query AND category selected
                  const filteredExams = INDIAN_EXAMS.filter(exam => {
                    const matchesSearch = exam.name.toLowerCase().includes(examSearch.toLowerCase()) || exam.description.toLowerCase().includes(examSearch.toLowerCase());
                    
                    let matchesCategory = false;
                    const id = exam.id.toLowerCase();
                    if (examFilter === "All") matchesCategory = true;
                    else if (examFilter === "SSC" && id.startsWith("ssc_")) matchesCategory = true;
                    else if (examFilter === "Railway" && id.startsWith("rrb_")) matchesCategory = true;
                    else if (examFilter === "Defense & Police" && id.match(/crpf|cisf|bsf|itbp|ssb|police/)) matchesCategory = true;
                    else if (examFilter === "Judiciary" && id.match(/hc|court|jja/)) matchesCategory = true;
                    else if (examFilter === "State Boards" && id.match(/upsssc|bihar|bssc|dsssb|jssc|hssc|cg_vyapam|rsmssb|raj_|cpct|uksssc/)) matchesCategory = true;
                    else if (examFilter === "Central Govt" && id.match(/epfo|kvs|nvs|fci|esic|drdo|central|cbi/)) matchesCategory = true;

                    return matchesSearch && matchesCategory;
                  });

                  const standardExams = filteredExams.filter(e => e.type === 'standard');
                  const sequentialExams = filteredExams.filter(e => e.type === 'sequential');

                  return (
                    <>
                      {/* Empty State Handler */}
                      {filteredExams.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                          <h3 style={{ fontSize: '20px', marginBottom: '10px' }}>No exams found</h3>
                          <p>Try adjusting your search or filter criteria.</p>
                        </div>
                      )}

                      {/* CATEGORY 1: Standard Exams */}
                      {standardExams.length > 0 && (
                        <div style={{ marginBottom: '50px' }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', color: isDarkMode ? '#f8fafc' : '#0f172a', borderBottom: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, paddingBottom: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span>Standard Mode Layouts</span>
                            <span style={{ fontSize: '13px', backgroundColor: isDarkMode ? '#1e293b' : '#e2e8f0', color: isDarkMode ? '#38bdf8' : '#0284c7', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' }}>Choose Single Stream</span>
                          </h3>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '24px' }}>
                            {standardExams.map((exam) => (
                              <div 
                                key={exam.id}
                                style={{ 
                                  backgroundColor: isDarkMode ? '#1e293b' : 'white', 
                                  border: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, 
                                  borderRadius: '20px', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                  boxShadow: '0 10px 25px rgba(0,0,0,0.03)', transition: 'transform 0.2s, border-color 0.2s'
                                }}
                              >
                                <div>
                                  <h4 style={{ color: isDarkMode ? '#f8fafc' : '#0f172a', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>{exam.name}</h4>
                                  <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '13px', lineHeight: '1.5', margin: '0 0 20px 0', minHeight: '55px' }}>{exam.description}</p>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 'auto' }}>
                                  <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: isDarkMode ? '#64748b' : '#94a3b8', letterSpacing: '0.5px', marginBottom: '2px' }}>Available Mediums:</div>
                                  {exam.allowedOptions.map((option) => (
                                    <button
                                      key={option.id}
                                      onClick={() => openExamInfo(exam.id, 10, null, option.lang)}
                                      style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
                                        color: isDarkMode ? '#38bdf8' : '#0284c7', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s'
                                      }}
                                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDarkMode ? '#38bdf8' : '#0284c7'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'transparent'; }}
                                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = isDarkMode ? '#0f172a' : '#f8fafc'; e.currentTarget.style.color = isDarkMode ? '#38bdf8' : '#0284c7'; e.currentTarget.style.borderColor = isDarkMode ? '#334155' : '#cbd5e1'; }}
                                    >
                                      <span>{option.label}</span>
                                      <span style={{ fontSize: '12px', fontWeight: '700', opacity: 0.9 }}>{option.speed} WPM</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CATEGORY 2: Sequential Bilingual Exams */}
                      {sequentialExams.length > 0 && (
                        <div id="sequential-track"> {/* 🚀 ADDED ID HERE */}
                          <h3 style={{ fontSize: '20px', fontWeight: '700', color: isDarkMode ? '#f8fafc' : '#0f172a', borderBottom: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, paddingBottom: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span>Sequential Bilingual Track</span>
                            <span style={{ fontSize: '11px', backgroundColor: '#f59e0b', color: 'white', padding: '3px 9px', borderRadius: '12px', fontWeight: '700', textTransform: 'uppercase' }}>Mandatory Both</span>
                          </h3>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '24px' }}>
                            {sequentialExams.map((exam) => (
                              <div 
                                key={exam.id}
                                style={{ 
                                  backgroundColor: isDarkMode ? '#1e293b' : 'white', 
                                  border: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, 
                                  borderRadius: '20px', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                  boxShadow: '0 10px 25px rgba(0,0,0,0.03)', transition: 'transform 0.2s, border-color 0.2s'
                                }}
                              >
                                <div>
                                  <h4 style={{ color: '#f59e0b', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>{exam.name}</h4>
                                  <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '13px', lineHeight: '1.5', margin: '0 0 20px 0', minHeight: '55px' }}>{exam.description}</p>
                                </div>

                                <div style={{ marginTop: 'auto' }}>
                                  {exam.allowedOptions.map((option) => (
                                    <button
                                      key={option.id}
                                      onClick={() => openExamInfo(exam.id, 10, null, 'sequential')}
                                      style={{
                                        width: '100%', padding: '12px 14px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center',
                                        border: 'none', backgroundColor: '#f59e0b', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
                                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
                                      }}
                                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f59e0b'; e.currentTarget.style.transform = 'none'; }}
                                    >
                                      <span>Launch Full Simulation</span>
                                      <span style={{ fontSize: '11px', fontWeight: '500', opacity: 0.9 }}>{option.label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

              </div>

             </motion.div>
           )}

           {/* TAB: TIPS & FAQS */}
            {menuTab === "tips" && (
              <motion.div 
                key="tips-faqs-page"
                initial={{ opacity: 0, y: -40, scale: 1.05 }} 
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}
              >
                <TipsAndFaqTab isDarkMode={isDarkMode} isMobile={isMobile} />
              </motion.div>
            )}

           {/* TAB: SHOP */}
           {menuTab === "shop" && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '30px', paddingBottom: '30px' }}>
              <div>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '24px', color: isDarkMode ? '#f8fafc' : '#0f172a' }}>Recommended Gear</h3>
                <motion.div key="shop-grid" variants={staggerContainer} initial="hidden" animate="show" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', width: '100%' }}>
                  <motion.div 
                    variants={slideInItem} 
                    style={{ 
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', 
                      borderRadius: '16px', 
                      padding: '16px', // Reduced padding to match the card look
                      border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      boxShadow: '0 4px 15px rgba(0,0,0,0.05)' 
                    }}
                  >
                    {/* IMAGE CONTAINER - No overflow hidden, allows the full banner look */}
                    <div style={{ width: '100%', height: '180px', borderRadius: '12px', marginBottom: '16px' }}>
                      <img 
                        src="/hp-keyboard.webp" 
                        alt="HP Wireless Keyboard" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} 
                      />
                    </div>
                    
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: '700' }}>
                      HP Wireless Keyboard
                    </h3>
                    <p style={{ margin: '0 0 20px 0', fontSize: '15px', color: isDarkMode ? '#94a3b8' : '#475569', flexGrow: 1, lineHeight: '1.5' }}>
                      Compact, spill-resistant, and perfect for practice.
                    </p>
                    <button 
                      onClick={() => window.open('https://amzn.to/3Q7Dt7k', '_blank')} 
                      style={{ width: '100%', padding: '14px', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      View on Amazon
                    </button>
                  </motion.div>

                  <motion.div 
                    variants={slideInItem} 
                    style={{ 
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', 
                      borderRadius: '16px', 
                      padding: '16px', 
                      border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      boxShadow: '0 4px 15px rgba(0,0,0,0.05)' 
                    }}
                  >
                    <div style={{ width: '100%', height: '180px', borderRadius: '12px', marginBottom: '16px' }}>
                      <img 
                        src="/wrist-rest.webp" 
                        alt="Ergonomic Wrist Rest" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} 
                      />
                    </div>
                    
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: '700' }}>
                      Ergonomic Wrist Rest
                    </h3>
                    <p style={{ margin: '0 0 20px 0', fontSize: '15px', color: isDarkMode ? '#94a3b8' : '#475569', flexGrow: 1, lineHeight: '1.5' }}>
                      Prevent fatigue during long coding and typing sessions.
                    </p>
                    <button 
                      onClick={() => window.open('https://amzn.to/4cxxfoy', '_blank')} 
                      style={{ width: '100%', padding: '14px', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      View on Amazon
                    </button>
                  </motion.div>
                </motion.div>
              </div>
            </div>
           )}

           {menuTab === "games" && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
              <Suspense fallback={<div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading arcade...</div>}>
                {activeGame === 'balloon' ? <BalloonGame isDarkMode={isDarkMode} onExit={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : activeGame === 'meteor' ? <MeteorGame isDarkMode={isDarkMode} onExit={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />  
                : activeGame === 'racer' ? <TypeRacerGame isDarkMode={isDarkMode} onExit={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : activeGame === 'keycap' ? <KeyCapSurvival isDarkMode={isDarkMode} typingLanguage={arcadeLanguage} onCancel={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : activeGame === 'slasher' ? <SyllableSlasher isDarkMode={isDarkMode} typingLanguage={arcadeLanguage} onCancel={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : activeGame === 'rhythm' ? <RhythmTypist isDarkMode={isDarkMode} typingLanguage={arcadeLanguage} onCancel={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : activeGame === 'bridge' ? <BridgeRunner isDarkMode={isDarkMode} typingLanguage={arcadeLanguage} onCancel={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : activeGame === 'tug' ? <TugOfWar isDarkMode={isDarkMode} typingLanguage={arcadeLanguage} onCancel={() => setActiveGame(null)} arcadeLevel={arcadeLevel} currentUser={currentUser} soundEnabled={soundEnabled} />
                : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '25px', flexWrap: 'wrap', gap: '20px' }}>
                      <div>
                        <h2 style={{ color: isDarkMode ? '#38bdf8' : '#0284c7', margin: '0 0 5px 0', fontSize: '28px' }}>Arcade</h2>
                        <p style={{ color: tStyle.subtext, margin: 0, fontSize: '16px' }}>Test your speed, accuracy, and survival skills.</p>
                      </div>

                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                        {/* 🌐 ARCADE LANGUAGE SELECTOR */}
                        <div style={{ display: 'flex', flexDirection: 'column', flex: isMobile ? '1 1 140px' : 'none' }}>
                          <label style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '6px' }}>Language</label>
                          <select 
                            value={arcadeLanguage} 
                            onChange={(e) => {
                              setArcadeLanguage(e.target.value);
                              localStorage.setItem('arcadeLang', e.target.value);
                            }} 
                            style={{ padding: '12px 16px', borderRadius: '10px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.itemBg, color: tStyle.colorText, fontWeight: 'bold', cursor: 'pointer', outline: 'none', width: '100%' }}
                          >
                            <option value="english">🇺🇸 English</option>
                            <option value="hindi">🇮🇳 Hindi</option>
                          </select>
                        </div>

                        {/* 📚 LESSON FILTER */}
                        <div style={{ display: 'flex', flexDirection: 'column', flex: isMobile ? '1 1 180px' : 'none' }}>
                          <label style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '6px' }}>Filter by Lesson</label>
                          <select 
                            value={arcadeLevel} 
                            onChange={(e) => setArcadeLevel(Number(e.target.value))} 
                            style={{ padding: '12px 18px', borderRadius: '10px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.itemBg, color: tStyle.colorText, fontWeight: 'bold', cursor: 'pointer', outline: 'none', width: '100%' }}
                          >
                            {Object.entries(ARCADE_LESSONS).map(([levelNum, data]) => <option key={levelNum} value={levelNum}>{data.title}</option> )}
                          </select>
                        </div>
                      </div>
                    </div>

                    <motion.div key="arcade-grid" variants={staggerContainer} initial="hidden" animate="show" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', overflowY: 'auto' }}>
                    
                      <motion.div variants={slideInItem} onClick={() => setActiveGame('balloon')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #38bdf8' : '2px solid #0ea5e9', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(2, 132, 199, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px' }}>🎈</div>
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Balloon Ascent</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Pop the balloons before they escape.</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('meteor')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #f97316' : '2px solid #ea580c', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(249, 115, 22, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px', transform: 'rotate(-45deg)' }}>☄️</div>
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Meteor Defense</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Type full words to destroy meteors.</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('racer')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #a855f7' : '2px solid #9333ea', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(168, 85, 247, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px' }}>🏎️</div>
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Type Racer</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Race against the clock by typing paragraphs.</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#a855f7', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('keycap')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #10b981' : '2px solid #059669', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px' }}>🔥</div>
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Key-Cap Survival</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Type the glowing key before the fuse burns out.</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('slasher')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #ef4444' : '2px solid #dc2626', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(239, 68, 68, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px' }}>⚔️</div> {/* 🚀 FIXED EMOJI */}
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Syllable Slasher</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Train your brain to type in chunks. Slice the combinations!</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('rhythm')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #d946ef' : '2px solid #c026d3', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(217, 70, 239, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px' }}>🎹</div>
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Rhythm Typist</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Train your cadence! Type the keys exactly to the beat.</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#d946ef', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('bridge')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #3b82f6' : '2px solid #1d4ed8', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.15)' }}>
                        <div style={{ fontSize: '50px', marginBottom: '15px' }}>🌉</div>
                        <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Bridge Runner</h3>
                        <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Train your peripheral tracking! Read ahead to build bridges.</p>
                        <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                      </motion.div>

                      <motion.div variants={slideInItem} onClick={() => setActiveGame('tug')} style={{ backgroundColor: tStyle.itemBg, border: isDarkMode ? '2px solid #eab308' : '2px solid #ca8a04', borderRadius: '16px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'pointer', boxShadow: '0 4px 15px rgba(234, 179, 8, 0.15)' }}>
                      <div style={{ fontSize: '50px', marginBottom: '15px' }}>👹</div>
                      <h3 style={{ margin: '0 0 10px 0', color: tStyle.colorText, fontSize: '20px' }}>Tug of War</h3>
                      <p style={{ color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5', flexGrow: 1 }}>Error recovery training. Don't panic when you make a typo!</p>
                      <button style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#eab308', color: '#000', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', width: '100%', fontSize: '15px' }}>▶ Play Now</button>
                    </motion.div>

                  </motion.div>
                </>
              )}
              </Suspense> 
              
            </div>
           )}

           {/* TAB: STATISTICS */}
           {menuTab === "statistics" && (
            <motion.div key="stats-page" variants={staggerContainer} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
              <motion.h2 variants={slideInItem} style={{ textAlign: 'center', color: tStyle.accentGreen, margin: '0 0 20px 0', fontSize: '28px' }}>{text.tabStats}</motion.h2>
              
              {/* 🚀 TOGGLE BUTTONS */}
              <motion.div variants={slideInItem} style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
                <button 
                  onClick={() => setStatsMode('learning')}
                  style={{ padding: '10px 24px', borderRadius: '12px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer', transition: '0.2s', backgroundColor: statsMode === 'learning' ? (isDarkMode ? '#38bdf8' : '#0284c7') : tStyle.itemBg, color: statsMode === 'learning' ? 'white' : tStyle.text, border: `2px solid ${statsMode === 'learning' ? 'transparent' : tStyle.border}` }}
                >
                  🎓 Learning Stats
                </button>
                <button 
                  onClick={() => setStatsMode('exams')}
                  style={{ padding: '10px 24px', borderRadius: '12px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer', transition: '0.2s', backgroundColor: statsMode === 'exams' ? (isDarkMode ? '#10b981' : '#059669') : tStyle.itemBg, color: statsMode === 'exams' ? 'white' : tStyle.text, border: `2px solid ${statsMode === 'exams' ? 'transparent' : tStyle.border}` }}
                >
                  🏛️ Indian Exam Stats
                </button>
                
                {/* THE NEW DOWNLOAD BUTTON */}
                <button 
                  onClick={downloadReportCard}
                  style={{ padding: '10px 24px', borderRadius: '12px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer', transition: '0.2s', backgroundColor: '#f59e0b', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)' }}
                >
                  🖨️ Download Report
                </button>

              </motion.div>

              {statsMode === 'learning' ? (
                /* 🎓 LEARNING STATS VIEW */
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  
                  {/* 🚀 NEW: DYNAMIC LAYOUT SELECTOR IN STATS TAB */}
                  <motion.div variants={slideInItem} style={{ display: 'flex', justifyContent: 'center', marginBottom: '25px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: tStyle.itemBg, padding: '8px 16px', borderRadius: '12px', border: `1px solid ${tStyle.border}`, boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: tStyle.subtext }}>Viewing Layout:</span>
                      <select 
                        value={courseLanguage} 
                        onChange={(e) => { 
                          const newLang = e.target.value;
                          setCourseLanguage(newLang); 
                          localStorage.setItem('courseLang', newLang); 
                        }} 
                        style={{ padding: '6px 12px', borderRadius: '6px', border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, color: tStyle.colorText, fontWeight: 'bold', cursor: 'pointer', outline: 'none', fontSize: '14px' }}
                      >
                        <option value="english">🇺🇸 English</option>
                        <option value="kruti_dev">🇮🇳 Hindi (Kruti Dev 010)</option>
                        <option value="inscript">🇮🇳 Hindi (Mangal InScript)</option>
                        <option value="remington_gail">🇮🇳 Hindi (Remington GAIL)</option>
                        <option value="remington_cbi">🇮🇳 Hindi (Remington CBI)</option>
                      </select>
                    </div>
                  </motion.div>

                  {/* STATS LOGIC */}
                  {statEntries.length === 0 ? (
                    <motion.div variants={slideInItem} style={{ margin: 'auto', color: tStyle.subtext, fontWeight: 'bold', fontSize: '18px', backgroundColor: tStyle.itemBg, padding: '30px', borderRadius: '16px', border: `2px dashed ${tStyle.border}`, textAlign: 'center' }}>
                      No data found for this layout. Complete a drill to unlock!
                    </motion.div>
                  ) : (
                    <>
                      <motion.div variants={slideInItem} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: tStyle.itemBg, border: `2px solid ${tStyle.border}`, borderRadius: '16px', padding: '20px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}><div style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>Passed</div><div style={{ fontSize: '28px', fontWeight: 'bold', color: tStyle.colorText }}>{totalDrills}</div></div>
                        <div style={{ backgroundColor: isDarkMode ? '#064e3b' : '#f0fdf4', border: isDarkMode ? '2px solid #059669' : '2px solid #bbf7d0', borderRadius: '16px', padding: '20px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}><div style={{ fontSize: '12px', color: tStyle.accentGreen, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>Avg Speed</div><div style={{ fontSize: '28px', fontWeight: 'bold', color: isDarkMode ? '#86efac' : '#15803d' }}>{avgWpm}</div></div>
                        <div style={{ backgroundColor: isDarkMode ? '#1e3a8a' : '#eff6ff', border: isDarkMode ? '2px solid #1d4ed8' : '2px solid #bfdbfe', borderRadius: '16px', padding: '20px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}><div style={{ fontSize: '12px', color: tStyle.accentBlue, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>Top Speed</div><div style={{ fontSize: '28px', fontWeight: 'bold', color: isDarkMode ? '#93c5fd' : '#1d4ed8' }}>{bestWpm}</div></div>
                        <div style={{ backgroundColor: isDarkMode ? '#713f12' : '#fefce8', border: isDarkMode ? '2px solid #a16207' : '2px solid #fef08a', borderRadius: '16px', padding: '20px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}><div style={{ fontSize: '12px', color: isDarkMode ? '#fde047' : '#854d0e', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>Avg Acc</div><div style={{ fontSize: '28px', fontWeight: 'bold', color: isDarkMode ? '#fef08a' : '#a16207' }}>{avgAcc}%</div></div>
                      </motion.div>
                      <motion.div variants={slideInItem} style={{ flexGrow: 1, backgroundColor: tStyle.itemBg, borderRadius: '16px', border: `2px solid ${tStyle.border}`, padding: '30px', position: 'relative', overflowX: 'auto', minHeight: '300px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                         <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={learningChartData} margin={{ top: 20, right: 30, left: -20, bottom: 0 }}>
                              <defs>
                                <linearGradient id="colorWpm" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={isDarkMode ? '#38bdf8' : '#0ea5e9'} stopOpacity={0.5}/>
                                  <stop offset="95%" stopColor={isDarkMode ? '#38bdf8' : '#0ea5e9'} stopOpacity={0}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} vertical={false} />
                              <XAxis dataKey="lesson" stroke={isDarkMode ? '#64748b' : '#94a3b8'} fontSize={12} tickLine={false} axisLine={false} tick={{ fill: tStyle.subtext }} />
                              <YAxis stroke={isDarkMode ? '#64748b' : '#94a3b8'} fontSize={12} tickLine={false} axisLine={false} tick={{ fill: tStyle.subtext }} />
                              <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', borderRadius: '12px', border: `1px solid ${tStyle.border}`, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} itemStyle={{ color: tStyle.accentBlue, fontWeight: 'bold' }} labelStyle={{ color: tStyle.subtext, fontWeight: 'bold', marginBottom: '5px' }} />
                              <Line type="monotone" dataKey="wpm" name="Speed (WPM)" stroke={isDarkMode ? '#38bdf8' : '#0ea5e9'} strokeWidth={4} dot={{ r: 5, fill: tStyle.itemBg, strokeWidth: 2 }} activeDot={{ r: 8, fill: tStyle.accentBlue, stroke: 'white', strokeWidth: 2 }} />
                            </LineChart>
                          </ResponsiveContainer>
                      </motion.div>
                    </>
                  )}
                </div>
              ) : (
                /* 🏛️ INDIAN EXAMS STATS VIEW */
                (() => {
                  const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
                  const myStats = db[currentUser]?.examStats || { attempts: 0, totalTime: 0, sumGross: 0, sumNet: 0, sumAcc: 0, sumErrorRate: 0 };
                  
                  if (myStats.attempts === 0) {
                    return <motion.div variants={slideInItem} style={{ margin: 'auto', color: tStyle.subtext, fontWeight: 'bold', fontSize: '18px', backgroundColor: tStyle.itemBg, padding: '30px', borderRadius: '16px', border: `2px dashed ${tStyle.border}`, textAlign: 'center' }}>Complete a Mock Exam to unlock your testing analytics!</motion.div>;
                  }

                  const attempts = myStats.attempts;
                  const hours = Math.floor(myStats.totalTime / 3600);
                  const mins = Math.floor((myStats.totalTime % 3600) / 60);
                  const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins} min`;
                  
                  const avgGross = Math.round(myStats.sumGross / attempts);
                  const avgNet = Math.round(myStats.sumNet / attempts);
                  const avgAcc = Math.round(myStats.sumAcc / attempts);
                  const avgErr = (myStats.sumErrorRate / attempts).toFixed(2);

                  return (
                    <motion.div variants={slideInItem} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                      
                      <div style={{ backgroundColor: tStyle.itemBg, border: `2px solid ${tStyle.border}`, borderRadius: '16px', padding: '30px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '13px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Total Exams Attempted</div>
                        <div style={{ fontSize: '38px', fontWeight: 'bold', color: isDarkMode ? '#38bdf8' : '#0284c7' }}>{attempts}</div>
                      </div>

                      <div style={{ backgroundColor: tStyle.itemBg, border: `2px solid ${tStyle.border}`, borderRadius: '16px', padding: '30px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '13px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Total Time Practicing</div>
                        <div style={{ fontSize: '38px', fontWeight: 'bold', color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>{timeStr}</div>
                      </div>

                      <div style={{ backgroundColor: isDarkMode ? '#064e3b' : '#f0fdf4', border: isDarkMode ? '2px solid #059669' : '2px solid #bbf7d0', borderRadius: '16px', padding: '30px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '13px', color: '#10b981', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Avg Net Speed</div>
                        <div style={{ fontSize: '38px', fontWeight: 'bold', color: isDarkMode ? '#86efac' : '#15803d' }}>{avgNet} <span style={{fontSize:'16px'}}>WPM</span></div>
                      </div>

                      <div style={{ backgroundColor: tStyle.itemBg, border: `2px solid ${tStyle.border}`, borderRadius: '16px', padding: '30px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '13px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Avg Gross Speed</div>
                        <div style={{ fontSize: '38px', fontWeight: 'bold', color: tStyle.text }}>{avgGross} <span style={{fontSize:'16px'}}>WPM</span></div>
                      </div>

                      <div style={{ backgroundColor: isDarkMode ? '#713f12' : '#fefce8', border: isDarkMode ? '2px solid #a16207' : '2px solid #fef08a', borderRadius: '16px', padding: '30px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '13px', color: isDarkMode ? '#fde047' : '#854d0e', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Avg Accuracy</div>
                        <div style={{ fontSize: '38px', fontWeight: 'bold', color: isDarkMode ? '#fef08a' : '#a16207' }}>{avgAcc}%</div>
                      </div>

                      <div style={{ backgroundColor: isDarkMode ? '#450a0a' : '#fef2f2', border: isDarkMode ? '2px solid #dc2626' : '2px solid #fecaca', borderRadius: '16px', padding: '30px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Avg Error Rate (SSC)</div>
                        <div style={{ fontSize: '38px', fontWeight: 'bold', color: isDarkMode ? '#fca5a5' : '#b91c1c' }}>{avgErr}%</div>
                      </div>

                    </motion.div>
                  );
                })()
              )}

            </motion.div>
           )}

           {/* TAB: SETTINGS */}
           {menuTab === "settings" && (
            <motion.div key="settings-page" variants={staggerContainer} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <motion.h2 variants={slideInItem} style={{ color: tStyle.text, margin: '0 0 30px 0', fontSize: '28px', borderBottom: `2px solid ${tStyle.border}`, paddingBottom: '15px' }}>⚙️ Preferences</motion.h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                
                {/* 1. ZEN MODE */}
                <motion.div variants={slideInItem} style={{ backgroundColor: tStyle.itemBg, padding: '20px 25px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${tStyle.border}`, boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', color: tStyle.text, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>🧘‍♂️ Zen Mode</h3>
                    <p style={{ margin: 0, color: tStyle.subtext, fontSize: '13px' }}>Hide live timer and speed metrics while typing to build deep focus.</p>
                  </div>
                  <button onClick={() => setZenMode(!zenMode)} style={{ padding: '8px 24px', borderRadius: '20px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: '0.2s', backgroundColor: zenMode ? '#10b981' : tStyle.border, color: zenMode ? 'white' : tStyle.text, border: 'none' }}>
                    {zenMode ? 'ON' : 'OFF'}
                  </button>
                </motion.div>

                {/* 2. KEYBOARD SOUNDS */}
                <motion.div variants={slideInItem} style={{ backgroundColor: tStyle.itemBg, padding: '20px 25px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${tStyle.border}`, boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', color: tStyle.text, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>⌨️ Keyboard Acoustics</h3>
                    <p style={{ margin: 0, color: tStyle.subtext, fontSize: '13px' }}>Choose your preferred typing sound profile.</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {/* 🚀 WIRED UP THE SOUND PROFILE SELECTOR */}
                    <select 
                      defaultValue={localStorage.getItem('soundProfile') || 'modern'}
                      onChange={(e) => {
                         localStorage.setItem('soundProfile', e.target.value);
                         // Play a test sound immediately so they can hear the change!
                         import('../Data/SoundManager').then(module => {
                            module.initAudio();
                            module.playKeystroke('standard');
                         });
                      }}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${tStyle.border}`, backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', color: tStyle.text, cursor: 'pointer', outline: 'none' }}
                    >
                      <option value="modern">Modern Pop</option>
                      <option value="mechanical">Mechanical Keyboard</option>
                      <option value="typewriter">Vintage Typewriter</option>
                    </select>
                    <button onClick={() => setSoundEnabled(!soundEnabled)} style={{ padding: '8px 24px', borderRadius: '20px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: '0.2s', backgroundColor: soundEnabled ? '#10b981' : tStyle.border, color: soundEnabled ? 'white' : tStyle.text, border: 'none' }}>
                      {soundEnabled ? 'ON' : 'MUTED'}
                    </button>
                  </div>
                </motion.div>

                {/* 3. CURSOR CUSTOMIZATION */}
                <motion.div variants={slideInItem} style={{ backgroundColor: tStyle.itemBg, padding: '20px 25px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${tStyle.border}`, boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', color: tStyle.text, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>🖱️ Caret Style</h3>
                    <p style={{ margin: 0, color: tStyle.subtext, fontSize: '13px' }}>Customize how your typing indicator looks.</p>
                  </div>
                  <select value={cursorStyle} onChange={(e) => setCursorStyle(e.target.value)} style={{ padding: '8px 15px', borderRadius: '8px', backgroundColor: tStyle.bg, color: tStyle.text, border: `1px solid ${tStyle.border}`, outline: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
                    <option value="line">| Line (Classic)</option>
                    <option value="block">█ Block</option>
                    <option value="underline">_ Underline</option>
                  </select>
                </motion.div>

                {/* 4. DATA MANAGEMENT */}
                <motion.div variants={slideInItem} style={{ backgroundColor: tStyle.itemBg, padding: '20px 25px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${tStyle.border}`, boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', color: tStyle.text, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>📊 Data Management</h3>
                    <p style={{ margin: 0, color: tStyle.subtext, fontSize: '13px' }}>Backup your progress or start fresh.</p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => {
                      const data = localStorage.getItem('typeFasterDB');
                      const blob = new Blob([data], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'MyTypingCoach_Backup.json'; a.click();
                    }} style={{ padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', backgroundColor: tStyle.bg, color: tStyle.text, border: `1px solid ${tStyle.border}` }}>
                      📥 Export Backup
                    </button>
                    <button 
                      onClick={async () => { 
                        if(window.confirm("Are you sure? This will wipe all progress for all keyboards!")) { 
                          // 1. Clear Local Storage
                          localStorage.removeItem('typeFasterDB'); 
                          
                          // 2. Clear Cloud Database if Logged In
                          if (currentUser) {
                            await supabase.from('users').update({ 
                              progress_data: { savedLevel: 0, completedLevels: [], stats: {}, achievements: [] } 
                            }).eq('email', currentUser);
                          }
                          
                          // 3. Reload App
                          window.location.reload(); 
                        } 
                      }} 
                      style={{ padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #ef4444' }}
                    >
                      🗑️ Reset
                    </button>
                  </div>
                </motion.div>

                {/* 5. DARK MODE (Existing) */}
                <motion.div variants={slideInItem} style={{ backgroundColor: tStyle.itemBg, padding: '20px 25px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${tStyle.border}`, boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', color: tStyle.text, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>🎨 Appearance</h3>
                    <p style={{ margin: 0, color: tStyle.subtext, fontSize: '13px' }}>Toggle sleek night theme.</p>
                  </div>
                  <button onClick={() => setIsDarkMode(!isDarkMode)} style={{ padding: '8px 24px', borderRadius: '20px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: '0.2s', backgroundColor: isDarkMode ? '#38bdf8' : '#0284c7', color: 'white', border: 'none' }}>
                    {isDarkMode ? '🌙 DARK' : '☀️ LIGHT'}
                  </button>
                </motion.div>

              </div>
            </motion.div>
           )}

           {/* ==========================================
               🏆 TAB: GLOBAL LEADERBOARD
               ========================================== */}
           {menuTab === "leaderboard" && (
            <motion.div key="lb-page" variants={staggerContainer} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
              <motion.h2 variants={slideInItem} style={{ textAlign: 'center', color: isDarkMode ? '#fde047' : '#ca8a04', margin: '0 0 20px 0', fontSize: '28px' }}>
                🏆 Global Leaderboard
              </motion.h2>

              {/* Leaderboard Toggles */}
              <motion.div variants={slideInItem} style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
                <button 
                  onClick={() => setLbCategory('exams')}
                  style={{ padding: '10px 24px', borderRadius: '12px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', transition: '0.2s', backgroundColor: lbCategory === 'exams' ? (isDarkMode ? '#38bdf8' : '#0284c7') : tStyle.itemBg, color: lbCategory === 'exams' ? 'white' : tStyle.text, border: `2px solid ${lbCategory === 'exams' ? 'transparent' : tStyle.border}` }}
                >
                  🏛️ Top Exam Candidates
                </button>
                <button 
                  onClick={() => setLbCategory('learning')}
                  style={{ padding: '10px 24px', borderRadius: '12px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', transition: '0.2s', backgroundColor: lbCategory === 'learning' ? (isDarkMode ? '#10b981' : '#059669') : tStyle.itemBg, color: lbCategory === 'learning' ? 'white' : tStyle.text, border: `2px solid ${lbCategory === 'learning' ? 'transparent' : tStyle.border}` }}
                >
                  🎓 Fastest Learners
                </button>
              </motion.div>

              {isLoadingLb ? (
                <div style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '50px', fontSize: '18px', fontWeight: 'bold' }}>
                  📡 Fetching live scores from the server...
                </div>
              ) : rankedUsers.length === 0 ? (
                <div style={{ textAlign: 'center', color: tStyle.subtext, marginTop: '50px', fontSize: '18px', fontWeight: 'bold' }}>
                  No scores recorded yet. Be the first to claim the top spot!
                </div>
              ) : (
                <motion.div variants={slideInItem} style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                  
                  {/* The Ranks List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Header Row */}
                    <div style={{ display: 'flex', padding: '10px 20px', fontSize: '12px', color: tStyle.subtext, textTransform: 'uppercase', fontWeight: 'bold', borderBottom: `2px solid ${tStyle.border}` }}>
                      <div style={{ width: '60px', textAlign: 'center' }}>Rank</div>
                      <div style={{ flex: 1 }}>Typist Name</div>
                      {lbCategory === 'exams' && <div style={{ width: '100px', textAlign: 'center' }}>Exams Taken</div>}
                      <div style={{ width: '120px', textAlign: 'right' }}>{lbCategory === 'exams' ? 'Avg Net WPM' : 'Top Speed'}</div>
                    </div>

                    {/* Data Rows */}
                    {rankedUsers.map((user, index) => {
                      const isTop3 = index < 3;
                      const rankColors = ['#fbbf24', '#94a3b8', '#b45309']; // Gold, Silver, Bronze
                      
                      return (
                        <div key={index} style={{ 
                          display: 'flex', alignItems: 'center', padding: '16px 20px', 
                          backgroundColor: isTop3 ? (isDarkMode ? '#1e293b' : '#f8fafc') : tStyle.itemBg, 
                          borderRadius: '12px', border: `1px solid ${isTop3 ? rankColors[index] : tStyle.border}`,
                          boxShadow: isTop3 ? `0 4px 15px ${rankColors[index]}20` : 'none',
                          transform: isTop3 ? 'scale(1.02)' : 'scale(1)',
                          zIndex: isTop3 ? 10 : 1
                        }}>
                          <div style={{ width: '60px', textAlign: 'center', fontSize: isTop3 ? '24px' : '16px', fontWeight: 'bold', color: isTop3 ? rankColors[index] : tStyle.subtext }}>
                            {index === 0 ? '👑' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                          </div>
                          
                          <div style={{ flex: 1, fontSize: isTop3 ? '18px' : '16px', fontWeight: 'bold', color: tStyle.text, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {user.username}
                            {user.email === currentUser && <span style={{ fontSize: '10px', backgroundColor: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: '10px' }}>YOU</span>}
                          </div>

                          {lbCategory === 'exams' && (
                            <div style={{ width: '100px', textAlign: 'center', color: tStyle.subtext, fontWeight: 'bold', fontSize: '14px' }}>
                              {user.examAttempts}
                            </div>
                          )}

                          <div style={{ width: '120px', textAlign: 'right', fontSize: isTop3 ? '22px' : '18px', fontWeight: 'bold', color: lbCategory === 'exams' ? '#10b981' : '#38bdf8' }}>
                            {lbCategory === 'exams' ? user.avgExamWpm : user.maxLearningWpm} <span style={{ fontSize: '12px', color: tStyle.subtext }}>WPM</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                </motion.div>
              )}
            </motion.div>
           )}

         </div>
      </main>

      {/* ========================================== */}
      {/* 🚀 4. THE PROFESSIONAL FOOTER                */}
      {/* ========================================== */}
      <footer style={{ backgroundColor: isDarkMode ? '#020617' : '#0f172a', padding: '60px 30px', color: '#cbd5e1', borderTop: `1px solid ${isDarkMode ? '#1e293b' : '#334155'}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '40px' }}>
          
          {/* BRAND & CONTACT SECTION */}
          <div style={{ flex: '1 1 300px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <img 
                onClick={() => {setMenuTab("home"); window.scrollTo(0,0);}} 
                src="/MTClogo.webp" 
                alt="My Typing Coach"
                width="500"
                height="500"
                style={{ 
                  width: '100px',
                  height: 'auto', 
                  cursor: 'pointer',
                  objectFit: 'contain'
                }} 
              />
              <h3 style={{ margin: 0, color: 'white', fontSize: '22px' }}>My Typing Coach</h3>
            </div>
            <p style={{ lineHeight: '1.6', color: '#94a3b8', marginBottom: '25px' }}>The ultimate typing platform for government exams, competitive speed building, and rhythmic mastery.</p>
            
            {/* Social Icons */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
               {/* Facebook */}
               <a href="https://www.facebook.com/share/1D2wFK3mjN/" aria-label="Follow us on Facebook" target="_blank" rel="noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '24px', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#1877f2'} onMouseLeave={e=>e.target.style.color='#94a3b8'}><i className="fa-brands fa-facebook"></i></a> 
               
               {/* Instagram */}
               <a href="https://www.instagram.com/mytypingcoach?igsh=bGl6a2ptMjNhN3ps" aria-label="Follow us on Instagram" target="_blank" rel="noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '24px', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#e1306c'} onMouseLeave={e=>e.target.style.color='#94a3b8'}><i className="fa-brands fa-instagram"></i></a> 
               
                {/* X (Formerly Twitter) */}
                <a 
                  href="https://x.com/MyTypingCoach"
                  aria-label="Follow us on X (Twitter)"
                  target="_blank" 
                  rel="noreferrer" 
                  style={{ 
                    color: '#94a3b8', 
                    textDecoration: 'none', 
                    width: '24px', 
                    height: '30px', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    transition: 'color 0.2s',
                    verticalAlign: 'middle'
                  }} 
                  onMouseEnter={e => e.currentTarget.style.color = '#ffffff'} 
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                >
                  <svg 
                    viewBox="0 3 23 24" 
                    aria-hidden="true" 
                    style={{ 
                      width: '21px', 
                      height: '21px', 
                      fill: 'currentColor',
                      display: 'block' 
                    }}
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                  </svg>
                </a>
               
               {/* YouTube */}
               <a href="https://youtube.com/@mytypingcoach?si=FEni2iQU6FR4gyui" aria-label="Follow us on YouTube" target="_blank" rel="noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '24px', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#ff0000'} onMouseLeave={e=>e.target.style.color='#94a3b8'}><i className="fa-brands fa-youtube"></i></a>
               
               {/* Telegram */}
               <a href="https://t.me/mytypingcoach" aria-label="Follow us on Telegram" target="_blank" rel="noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '24px', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#0088cc'} onMouseLeave={e=>e.target.style.color='#94a3b8'}><i className="fa-brands fa-telegram"></i></a>
            </div>

            {/* DIRECT CONTACT LINKS */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a href="https://wa.me/918873900803" aria-label="Chat with us on WhatsApp" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#94a3b8', textDecoration: 'none', fontSize: '15px', fontWeight: '500', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#25D366'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>
                <i className="fa-brands fa-whatsapp" style={{ fontSize: '20px' }}></i> +91 8873900803
              </a>
              <a href="mailto:mytypingcoach17@gmail.com" aria-label="Send us an email" style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#94a3b8', textDecoration: 'none', fontSize: '15px', fontWeight: '500', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#ea4335'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>
                <i className="fa-solid fa-envelope" style={{ fontSize: '18px' }}></i> mytypingcoach17@gmail.com
              </a>
            </div>
          </div>

          {/* QUICK LINKS */}
          <div style={{ flex: '1 1 150px' }}>
            <h4 style={{ color: 'white', fontSize: '18px', marginBottom: '25px' }}>Quick Links</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', color: '#94a3b8' }}>
              <span onClick={() => {setMenuTab("exams"); window.scrollTo(0,0);}} style={{ cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#38bdf8'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Indian Exams</span>
              <span onClick={() => {setMenuTab("statistics"); window.scrollTo(0,0);}} style={{ cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#38bdf8'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Check Results</span>
              <span onClick={() => {setMenuTab("shop"); window.scrollTo(0,0);}} style={{ cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#38bdf8'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Typing Gear</span>
              <span onClick={() => {setMenuTab("upgrade"); window.scrollTo(0,0);}} style={{ cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='#38bdf8'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Pricing & Plans</span>
            </div>
          </div>

          {/* LEGALS */}
          <div style={{ flex: '1 1 150px' }}>
            <h4 style={{ color: 'white', fontSize: '18px', marginBottom: '25px' }}>Legals</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', color: '#94a3b8' }}>
              {/* 🚀 LEGAL LINKS OPEN IN NEW TAB */}
              <a href="/about.html" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='white'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>About Us</a>
              <a href="/declaration.html" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='white'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Declaration</a>
              <a href="/terms.html" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='white'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Terms of Service</a>
              <a href="/privacy.html" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e=>e.target.style.color='white'} onMouseLeave={e=>e.target.style.color='#94a3b8'}>Privacy Policy</a>
            </div>
          </div>
        </div>
        
        <div style={{ maxWidth: '1100px', margin: '40px auto 0 auto', borderTop: '1px solid #334155', paddingTop: '25px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
          Copyright © {new Date().getFullYear()} My Typing Coach. All rights reserved.
        </div>
      </footer>
      <KeyboardTipModal 
        isOpen={showKeyboardTip} 
        onClose={() => setShowKeyboardTip(false)} 
        isDarkMode={isDarkMode} 
        layoutName={tipLayoutName} 
      />

      {/* ========================================== */}
      {/* 🖨️ HIDDEN PDF REPORT CARD TEMPLATE       */}
      {/* ========================================== */}
      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
        <div 
          id="official-report-card" 
          style={{ 
            width: '210mm', minHeight: '297mm', padding: '60px', 
            backgroundColor: '#ffffff', color: '#0f172a',
            fontFamily: 'Arial, sans-serif', display: 'none', boxSizing: 'border-box'
          }}
        >
          {/* Header */}
          <div style={{ borderBottom: '3px solid #1e3a8a', paddingBottom: '20px', marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '32px', color: '#1e3a8a', margin: '0 0 10px 0' }}>My Typing Coach</h1>
              <p style={{ margin: '0', fontSize: '16px', color: '#64748b' }}>Official Performance Evaluation</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Date: {new Date().toLocaleDateString()}</p>
              <p style={{ margin: '0', color: '#64748b' }}>ID: {Math.random().toString(36).substr(2, 9).toUpperCase()}</p>
            </div>
          </div>

          {/* User Details */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '20px', color: '#334155', borderBottom: '1px solid #cbd5e1', paddingBottom: '10px' }}>Candidate Details</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px', fontSize: '16px' }}>
              <div style={{ display: 'flex' }}><span style={{ width: '150px', fontWeight: 'bold' }}>Name:</span> <span>{profileData.name || 'Guest'}</span></div>
              <div style={{ display: 'flex' }}><span style={{ width: '150px', fontWeight: 'bold' }}>Email:</span> <span>{currentUser || 'Guest User'}</span></div>
              <div style={{ display: 'flex' }}><span style={{ width: '150px', fontWeight: 'bold' }}>Status:</span> <span style={{ color: isPremium ? '#10b981' : '#64748b', fontWeight: 'bold' }}>{isPremium ? 'Premium Member' : 'Free Tier'}</span></div>
            </div>
          </div>

          {/* Performance Data */}
          <div style={{ marginBottom: '60px' }}>
            <h2 style={{ fontSize: '20px', color: '#334155', borderBottom: '1px solid #cbd5e1', paddingBottom: '10px' }}>Typing Metrics</h2>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px', marginTop: '20px' }}>
              
              {/* Box 1: Learning Module Performance */}
              <div style={{ flex: 1, backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 5px 0', color: '#1e3a8a', fontSize: '18px' }}>Learning Module Performance</h3>
                <div style={{ fontSize: '14px', color: '#059669', fontWeight: 'bold', marginBottom: '15px' }}>
                  Layout: {courseLanguage === 'english' ? 'English' : courseLanguage === 'kruti_dev' ? 'Hindi (Kruti Dev 010)' : courseLanguage === 'inscript' ? 'Hindi (Mangal InScript)' : courseLanguage === 'remington_gail' ? 'Hindi (Remington GAIL)' : 'Hindi (Remington CBI)'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Top Speed:</span> <strong>{bestWpm || 0} WPM</strong></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Average Speed:</span> <strong>{avgWpm || 0} WPM</strong></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Average Accuracy:</span> <strong>{avgAcc || 0}%</strong></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Lessons Passed:</span> <strong>{totalDrills || 0}</strong></div>
                </div>
              </div>

              {/* 🚀 Box 2: NEW Indian Exam Stats */}
              {(() => {
                const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
                const myStats = db[currentUser]?.examStats || { attempts: 0, totalTime: 0, sumGross: 0, sumNet: 0, sumAcc: 0, sumErrorRate: 0 };
                const attempts = myStats.attempts;
                const hours = Math.floor(myStats.totalTime / 3600);
                const mins = Math.floor((myStats.totalTime % 3600) / 60);
                const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins} min`;
                const avgGross = attempts > 0 ? Math.round(myStats.sumGross / attempts) : 0;
                const avgNet = attempts > 0 ? Math.round(myStats.sumNet / attempts) : 0;
                const avgAcc = attempts > 0 ? Math.round(myStats.sumAcc / attempts) : 0;
                
                return (
                  <div style={{ flex: 1, backgroundColor: '#f0fdf4', padding: '20px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <h3 style={{ margin: '0 0 5px 0', color: '#064e3b', fontSize: '18px' }}>Official Exam Simulations</h3>
                    <div style={{ fontSize: '14px', color: '#059669', fontWeight: 'bold', marginBottom: '15px' }}>
                      Aggregate Performance
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '16px', color: '#064e3b' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Attempts:</span> <strong>{attempts}</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Practice Time:</span> <strong>{timeStr}</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Avg Net Speed:</span> <strong>{avgNet} WPM</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Avg Accuracy:</span> <strong>{avgAcc}%</strong></div>
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>

          {/* Official Signature */}
          <div style={{ marginTop: 'auto', paddingTop: '60px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'center', width: '250px' }}>
              <img 
                src="/signature.webp" 
                alt="Signature" 
                style={{ width: '100%', height: 'auto', marginBottom: '10px', objectFit: 'contain' }} 
                onError={(e) => { e.target.style.display = 'none'; }} 
              />
              <div style={{ borderTop: '2px solid #0f172a', paddingTop: '10px' }}>
                <p style={{ margin: '0', fontWeight: 'bold', fontSize: '18px' }}>Aryan Kumar</p>
                <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '14px' }}>Founder & Lead Instructor<br/>My Typing Coach</p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MenuDashboard;